/**
 * ProductSelectionModal — Reusable modal for selecting linked products.
 *
 * Two modes:
 *   1. "search" (default) — Dynamic products from API:
 *        POST /datatable/incoming/{userType}/{userId}/signage/{signageCompanyId}
 *   2. "Browse Catalog" — Static data for Brands / Categories / Products
 *        (to be wired to real APIs later)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import PaginationControls from "components/PaginationControls";
import "../assets/css/SignageOrders.css";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import {
  getImageSrc,
  pickRawImageValue,
  resolvePresignedImageSrc,
  usePresignedMap,
  useVariantImageUrlMap,
} from "components/Tableimageshelpers";
import { uploadFilesAndGetIds } from "components/Imageshelper";
import {
  getPostTypeSlotCatalog,
  getPostTypeSlotBrandsSelect2,
  getPostTypeSlotCategoriesSelect2,
  getPostTypeSlotBrandCategoriesSelect2,
  getPostTypeSlotBrandCategoryProductsDatatable,
  getPostTypeSlotCategoryProductsDatatable,
  getPostTypeSlotAllProductsDatatable,
  getPostTypeSlotSelect2,
  getPostTypeSlotProductsDatatable,
  getPostTypeAccessoryProductsDatatable,
  getPostTypeAccessoryBrandCategoryProducts,
  getPostTypeAccessoryCategoryProducts,
  getAccessoryRulesForUser,
  summarizeAccessoryRuleCatalog,
  getPostTypeAccessoriesBrandsSelect2,
  getPostTypeAccessoriesCategoriesSelect2,
  getPostTypeAccessoriesBrandCategoriesSelect2,
  type SlotSelect2Brand,
  type SlotSelect2Category,
  type SlotProductDatatableRow,
  type AccessoryBrandCategoryProductsRawRow,
} from "api/postTypeApiCalls";
import { createCustomizationContext, instantiateDesignFromTemplate } from "api/realesyStudioApi";
import type { StudioBatchLineItem, StudioCustomizationFlowProps } from "components/ordercheckoutpop";
import { showError } from "toast/toast";

/* ────────────────────── Types ────────────────────── */

/** Shape for installation order `line_items[].customizations` (catalog / variant template answers). */
export type InstallationOrderLineCustomizations = {
  _id: string;
  template_name: string;
  fields: Array<{ _id: string; field_name: string; field_type: string; value: string }>;
};

export interface LinkedProduct {
  product_id: string;
  inventory_source: string;
  name: string;
  sku: string;
  category: string;
  images: Array<{ url: string; alt_text?: string; default?: boolean }>;
  current_status: string;
  available_quantity: number;
  linked_mode: string;
  location: Array<{
    quantity: number;
    warehouse_id: string;
    warehouse_name?: string;
    zone_id?: string;
    zone_name?: string;
    rack_id?: string;
    rack_name?: string;
    bin_id?: string;
    bin_name?: string;
  }>;
  created_at: string;
  /** Catalog/new product: variant id and price for line_items without fetch */
  variant_id?: string;
  retail_price?: number;
  /** Product is taxable for manual tax when TaxCloud not integrated */
  is_taxable?: boolean;
  /** When adding from a slot with an active Studio batch line, pass through for line_items + slot preview. */
  design_id?: string;
  design_name?: string;
  /** Work-drive preview file id (same as batch `preview_id`). */
  preview_url?: string;
  /** Filled from Browse Catalog customization when user adds the row (installation payload). */
  customizations?: InstallationOrderLineCustomizations;
}

/** Lines from parent `selectedProducts` used for Avl Qty consumption in this modal. */
export type SelectedStockLine = {
  product_id: string;
  variant_id?: string | null;
  quantity?: number;
};

/** When opening from a slot, pass slotId so Browse Catalog can fetch catalog counts from API */
export interface ProductSelectionModalProps {
  open: boolean;
  title?: string;
  onProductSelect: (product: LinkedProduct) => void;
  onClose: () => void;
  userType: "agent" | "office" | null;
  userId: string;
  signageCompanyId: string;
  token: string;
  selectedProductId?: string | null;
  /** Slot _id from post type detail — when set, Browse Catalog calls GET .../post-type-slots/{slotId} for counts */
  slotId?: string | null;
  /** Post type id — required with slotId to fetch slot brands/categories via .../post-type-slots/{slotId}/select2 */
  postTypeId?: string | null;
  /** When opening for Add Accessory (no slot), pass counts from getAccessoryRules so Browse Catalog shows them */
  accessoryCatalogCounts?: { total_brands: number; total_categories: number; total_products: number } | null;
  /** Category ids from accessory rules — used with bulk-by-ids until categories select2 is migrated */
  accessoryCategoryIds?: string[] | null;
  /** True while accessory rules are loading (Add Accessory flow) */
  accessoryCatalogLoading?: boolean;
  /** End-user id for GET .../accessory-rules?user_id= — Browse Catalog refetches counts + brand/category ids (Add Accessory, no slot). */
  accessoryRulesUserId?: string | null;
  /** When set (signage flows), opening Studio creates a customization context with order/installation snapshots. */
  studioCustomization?: StudioCustomizationFlowProps;
  /**
   * Products already added to the installation (slots, accessories, manual picks).
   * Used to decrease displayed Avl Qty and disable Add when remaining hits 0
   * for the same product_id + variant_id.
   */
  selectedStockLines?: SelectedStockLine[];
  /**
   * Edit mode only: original order line quantities for product_id + variant_id.
   * API available often excludes stock already on this order; baseline adds that
   * qty back so removing a line item increases Avl Qty correctly.
   */
  baselineStockLines?: SelectedStockLine[];
}

/** Stable key for product + variant stock tracking. */
export function productVariantStockKey(productId: string, variantId?: string | null): string {
  return `${String(productId ?? "").trim()}::${String(variantId ?? "").trim()}`;
}

/** Sum selected quantities per product_id + variant_id. */
export function buildConsumedQtyByProductVariant(
  lines: SelectedStockLine[] | undefined | null
): Map<string, number> {
  const map = new Map<string, number>();
  for (const line of lines || []) {
    const productId = String(line?.product_id ?? "").trim();
    if (!productId) continue;
    const key = productVariantStockKey(productId, line?.variant_id);
    const rawQty = Number(line?.quantity);
    const qty = Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 1;
    map.set(key, (map.get(key) || 0) + qty);
  }
  return map;
}

/**
 * Remaining available for Add:
 *   create: API qty − currently selected
 *   edit:   API qty + original order qty − currently selected
 * (so removing an existing line item returns that qty to Avl.)
 */
export function remainingAvailableQty(
  baseAvailable: number,
  productId: string,
  variantId: string | null | undefined,
  consumedByKey: Map<string, number>,
  baselineByKey?: Map<string, number>
): number {
  const base = Math.max(0, Number(baseAvailable) || 0);
  const key = productVariantStockKey(productId, variantId);
  const used = consumedByKey.get(key) || 0;
  const baseline = baselineByKey?.get(key) || 0;
  return Math.max(0, base + baseline - used);
}

/* ────────────── Static catalog data (placeholder) ────────────── */

type CatalogView = "search" | "picker" | "brands" | "allCategories" | "allProducts" | "categories" | "products";

interface Brand { id: string; name: string; logo: string; color: string; bg: string; categories?: string[]; product_count?: number; image?: string; }
interface Category { id: string; name: string; icon: string; color: string; bg: string; description: string; product_count?: number; image?: string; }
interface CatalogProduct { id: string; name: string; sku: string; brand: string; category: string; inStock: boolean; hasImage: boolean; image: string; }

const CATALOG_PRODUCTS: CatalogProduct[] = [
  { id: "P001", name: "Century 21 Main Panel", sku: "C21-MP-001", brand: "Century 21", category: "Main Panels", inStock: true, hasImage: true, image: "🏠" },
  { id: "P002", name: "Century 21 For Sale Rider", sku: "C21-RD-001", brand: "Century 21", category: "Riders", inStock: true, hasImage: true, image: "🏠" },
  { id: "P003", name: "Century 21 Open House", sku: "C21-DR-001", brand: "Century 21", category: "Directional Signs", inStock: true, hasImage: false, image: "🏠" },
  { id: "P004", name: "RE/MAX Main Panel", sku: "RMX-MP-001", brand: "RE/MAX", category: "Main Panels", inStock: true, hasImage: true, image: "🔴" },
  { id: "P005", name: "RE/MAX Sold Rider", sku: "RMX-RD-001", brand: "RE/MAX", category: "Riders", inStock: true, hasImage: true, image: "🔴" },
  { id: "P006", name: "RE/MAX Coming Soon", sku: "RMX-RD-002", brand: "RE/MAX", category: "Riders", inStock: false, hasImage: false, image: "🔴" },
  { id: "P007", name: "Keller Williams Panel", sku: "KW-MP-001", brand: "Keller Williams", category: "Main Panels", inStock: false, hasImage: false, image: "🟥" },
  { id: "P008", name: "Keller Williams Rider", sku: "KW-RD-001", brand: "Keller Williams", category: "Riders", inStock: true, hasImage: true, image: "🟥" },
  { id: "P009", name: "Coldwell Banker Panel", sku: "CB-MP-001", brand: "Coldwell Banker", category: "Main Panels", inStock: true, hasImage: true, image: "⭐" },
  { id: "P010", name: "Coldwell Banker Commercial", sku: "CB-CM-001", brand: "Coldwell Banker", category: "Commercial", inStock: true, hasImage: true, image: "⭐" },
  { id: "P011", name: "Compass For Sale Panel", sku: "CMP-MP-001", brand: "Compass", category: "Main Panels", inStock: true, hasImage: true, image: "🧭" },
  { id: "P012", name: "Compass Luxury Panel", sku: "CMP-LX-001", brand: "Compass", category: "Luxury", inStock: true, hasImage: true, image: "🧭" },
  { id: "P013", name: "Compass Rider", sku: "CMP-RD-001", brand: "Compass", category: "Riders", inStock: true, hasImage: false, image: "🧭" },
  { id: "P014", name: "Compass Open House", sku: "CMP-DR-001", brand: "Compass", category: "Directional Signs", inStock: false, hasImage: false, image: "🧭" },
  { id: "P015", name: "Sotheby's Main Panel", sku: "SB-MP-001", brand: "Sotheby's", category: "Main Panels", inStock: true, hasImage: true, image: "💎" },
  { id: "P016", name: "Sotheby's Luxury Sign", sku: "SB-LX-001", brand: "Sotheby's", category: "Luxury", inStock: true, hasImage: true, image: "💎" },
  { id: "P017", name: "Berkshire Hathaway Panel", sku: "BH-MP-001", brand: "Berkshire Hathaway", category: "Main Panels", inStock: true, hasImage: true, image: "🟤" },
  { id: "P018", name: "Berkshire Hathaway Rider", sku: "BH-RD-001", brand: "Berkshire Hathaway", category: "Riders", inStock: false, hasImage: false, image: "🟤" },
  { id: "P019", name: "eXp Realty Main Panel", sku: "EXP-MP-001", brand: "eXp Realty", category: "Main Panels", inStock: true, hasImage: true, image: "🔵" },
  { id: "P020", name: "eXp Realty Rider", sku: "EXP-RD-001", brand: "eXp Realty", category: "Riders", inStock: true, hasImage: false, image: "🔵" },
];

const STYLE_OPTIONS = ["For Sale", "For Rent", "For Lease", "Open House", "Coming Soon", "Sold", "Price Reduced"];

/** Build attribute key → list of values from slot product variants (same idea as ordercheckoutpop) */
function buildSlotAvailableAttributes(
  variants: SlotProductDatatableRow["product"]["variants"] = []
): Record<string, string[]> {
  const map: Record<string, Set<string>> = {};
  for (const v of variants) {
    const attrs = (v as { attributes?: Record<string, string> })?.attributes ?? {};
    for (const [k, val] of Object.entries(attrs)) {
      (map[k] ??= new Set()).add(String(val).trim());
    }
  }
  const out: Record<string, string[]> = {};
  for (const k of Object.keys(map)) out[k] = Array.from(map[k]);
  return out;
}

/** Find variant matching selected attributes */
function findSlotVariantByAttributes(
  variants: SlotProductDatatableRow["product"]["variants"] = [],
  selAttrs: Record<string, string>
): (typeof variants)[number] | undefined {
  return variants.find((v) => {
    const attrs = (v as { attributes?: Record<string, string> })?.attributes ?? {};
    return Object.entries(selAttrs).every(
      ([k, v]) => String(attrs[k] ?? "").trim() === String(v).trim()
    );
  });
}

/** Whether any variant has customization fields */
function slotProductHasCustomization(
  variants: SlotProductDatatableRow["product"]["variants"] = []
): boolean {
  return variants.some(
    (v) => Array.isArray((v as { customizations?: { fields?: unknown[] } })?.customizations?.fields) &&
      (v as { customizations?: { fields?: unknown[] } }).customizations!.fields!.length > 0
  );
}

/** Design-linked product: variant has Realesy Studio template_id (even if customizations.fields is empty). */
function variantHasStudioTemplateId(v: unknown): boolean {
  const tid = (v as { template_id?: string } | undefined)?.template_id;
  return typeof tid === "string" && tid.trim().length > 0;
}

/** Match GET /customization-batches/:id item to slot row + variant (same product_id / variant_id as line_items). */
function findStudioBatchLineItem(
  items: StudioBatchLineItem[] | undefined,
  productId: string,
  variantId: string
): StudioBatchLineItem | undefined {
  if (!items?.length) return undefined;
  const pid = String(productId ?? "").trim();
  const vid = String(variantId ?? "").trim();
  if (!pid || !vid) return undefined;
  return items.find(
    (it) => String(it.product_id ?? "").trim() === pid && String(it.variant_id ?? "").trim() === vid
  );
}

/** Customization field from API (variant.customizations.fields[]) */
type SlotCustomizationField = { field_name: string; field_type?: "text" | "file"; required?: boolean; _id?: string };

/** Get customization fields for the selected variant (or first variant with fields), like ordercheckoutpop */
function getSlotCustomizationFields(
  selectedVariant: NonNullable<SlotProductDatatableRow["product"]["variants"]>[number] | undefined,
  variants: SlotProductDatatableRow["product"]["variants"] = []
): SlotCustomizationField[] {
  const fields = (selectedVariant as { customizations?: { fields?: SlotCustomizationField[] } })?.customizations?.fields;
  if (Array.isArray(fields) && fields.length > 0) return fields;
  const first = variants[0] as { customizations?: { fields?: SlotCustomizationField[] } } | undefined;
  return Array.isArray(first?.customizations?.fields) ? first.customizations.fields : [];
}

function fieldCustomizationValueSatisfied(field: SlotCustomizationField, value: string | File | undefined): boolean {
  if ((field.field_type ?? "text") === "text") {
    return typeof value === "string" && value.trim().length > 0;
  }
  if (value instanceof File) return true;
  return typeof value === "string" && value.trim().length > 0;
}

const NULL_OBJECT_ID = "000000000000000000000000";

/** Build `line_items.customizations` from variant template + user-entered values (by field name). */
function buildInstallationLineCustomizations(
  variant: NonNullable<SlotProductDatatableRow["product"]["variants"]>[number],
  valuesByFieldName: Record<string, string>
): InstallationOrderLineCustomizations | undefined {
  const cust = (variant as { customizations?: { _id?: string; template_name?: string; fields?: SlotCustomizationField[] } })
    .customizations;
  const defs = Array.isArray(cust?.fields) ? cust.fields : [];
  if (defs.length === 0) return undefined;
  const v = variant as { variant_name?: string };
  const templateName = String(cust?.template_name ?? "").trim() || v.variant_name || "";
  return {
    _id: String(cust?._id ?? "").trim() || NULL_OBJECT_ID,
    template_name: templateName,
    fields: defs.map((f) => ({
      _id: String(f._id ?? "").trim() || NULL_OBJECT_ID,
      field_name: f.field_name,
      field_type: f.field_type === "file" ? "file" : "text",
      value: valuesByFieldName[f.field_name] != null ? String(valuesByFieldName[f.field_name]) : "",
    })),
  };
}

/** Map slot datatable row + selected variant to LinkedProduct for onProductSelect */
function slotRowToLinkedProduct(
  row: SlotProductDatatableRow,
  variant: NonNullable<SlotProductDatatableRow["product"]["variants"]>[number],
  batchMatch?: StudioBatchLineItem,
  /** When set (including `{}`), merge template fields with these answers for `customizations`. Omit for image-only previews. */
  customizationValuesByFieldName?: Record<string, string>
): LinkedProduct {
  const inv = (variant as { inventory?: Array<{ available?: number; warehouse_id?: string; warehouse_name?: string; zone_id?: string; zone_name?: string; rack_id?: string; rack_name?: string; bin_id?: string; bin_name?: string }> })?.inventory ?? [];
  const totalAvailable = inv.reduce((s, i) => s + (Number(i.available) || 0), 0);
  const location = inv.map((i) => ({
    quantity: Number(i.available) || 0,
    warehouse_id: i.warehouse_id ?? "",
    warehouse_name: i.warehouse_name,
    zone_id: i.zone_id,
    zone_name: i.zone_name,
    rack_id: i.rack_id,
    rack_name: i.rack_name,
    bin_id: i.bin_id,
    bin_name: i.bin_name,
  }));
  const images = ((variant as { images?: Array<{ url: string; alt_text?: string; default?: boolean }> })?.images ?? []).map((img) => ({
    url: img.url,
    alt_text: img.alt_text,
    default: img.default,
  }));
  const v = variant as { variant_id?: string; retail_price?: number; variant_name?: string; sku?: string };
  const base: LinkedProduct = {
    product_id: row._id,
    inventory_source: "",
    name: v.variant_name ?? row.product?.category ?? "Product",
    sku: v.sku ?? "",
    category: row.product?.category ?? "",
    images,
    current_status: "available",
    available_quantity: totalAvailable,
    linked_mode: "catalog",
    location,
    created_at: new Date().toISOString(),
    variant_id: v.variant_id,
    retail_price: v.retail_price != null ? Number(v.retail_price) : undefined,
    is_taxable: !!(row.product as { is_taxable?: boolean })?.is_taxable,
  };
  const customizations =
    customizationValuesByFieldName !== undefined
      ? buildInstallationLineCustomizations(variant, customizationValuesByFieldName)
      : undefined;
  const withCustom = customizations ? { ...base, customizations } : base;

  const did = String(batchMatch?.design_id ?? "").trim();
  const purl = String(batchMatch?.preview_id ?? "").trim();
  const dname = String(batchMatch?.design_name ?? "").trim();
  if (!did) return withCustom;
  return {
    ...withCustom,
    design_id: did,
    ...(dname ? { design_name: dname } : {}),
    ...(purl ? { preview_url: purl, images: [{ url: purl, default: true }] } : {}),
  };
}

/** Normalize products_user_filter/datatable raw row to SlotProductDatatableRow for reuse in product table.
 * API returns dot-notation keys: product.name, product.sku, product.brand, product.category, product.variants, etc. */
function normalizeAccessoryBrandCategoryProductRow(
  raw: AccessoryBrandCategoryProductsRawRow,
  categoryName: string,
  brandName?: string
): SlotProductDatatableRow {
  const variants = raw["product.variants"] ?? [];
  const firstVariant = Array.isArray(variants) && variants.length > 0 ? variants[0] : undefined;
  return {
    _id: raw._id ?? (raw.product_id as string) ?? "",
    product: {
      name: raw["product.name"] ?? (firstVariant as { variant_name?: string } | undefined)?.variant_name,
      sku: raw["product.sku"] ?? (firstVariant as { sku?: string } | undefined)?.sku,
      brand: brandName ?? raw["product.brand"],
      brand_id: raw["product.brand_id"],
      category: categoryName ?? raw["product.category"],
      category_id: raw["product.category_id"],
      is_taxable: Boolean(raw["product.is_taxable"]),
      variants,
    },
  };
}

/* ────────────────────── Helpers ────────────────────── */

const getCatStyle = (cat: string): { bg: string; color: string } => {
  const lower = cat.toLowerCase();
  if (lower.includes("main")) return { bg: "#dcfce7", color: "#16a34a" };
  if (lower.includes("rider")) return { bg: "#fef3c7", color: "#d97706" };
  if (lower.includes("commercial")) return { bg: "#dbeafe", color: "#2563eb" };
  if (lower.includes("directional")) return { bg: "#cffafe", color: "#0891b2" };
  if (lower.includes("luxury")) return { bg: "#ede9fe", color: "#7c3aed" };
  if (lower.includes("yard")) return { bg: "#dcfce7", color: "#16a34a" };
  if (lower.includes("wood")) return { bg: "#fef3c7", color: "#d97706" };
  return { bg: "#f1f5f9", color: "#64748b" };
};

const slotProductColumnHelper = createColumnHelper<SlotProductDatatableRow>();

/* ────────────────────── Component ────────────────────── */

/** Catalog counts from GET .../post-type-slots/{slotId} — used in Browse Catalog picker */
type SlotCatalogCounts = { total_brands: number; total_categories: number; total_products: number };

const SLOT_SELECT2_PAGE_SIZE = 20;

const catalogSearchInputStyle: React.CSSProperties = {
  width: "100%",
  padding: "11px 16px 11px 40px",
  border: "1px solid #9ea3aa",
  borderRadius: 24,
  fontSize: 13,
  outline: "none",
  background: "#fff",
  boxSizing: "border-box",
  color: "#334155",
};

function CatalogSearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#94a3b8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CatalogCenteredSearch({
  value,
  committedValue,
  onChange,
  onCommit,
  placeholder,
}: {
  value: string;
  committedValue: string;
  onChange: (next: string) => void;
  onCommit: (next: string) => void;
  placeholder: string;
}) {
  return (
    <div className="psm-catalog-search-row" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
      <div style={{ flex: 1, minWidth: 0 }} />
      <div className="psm-search-input-wrap" style={{ position: "relative", width: "100%", maxWidth: 380, flexShrink: 0 }}>
        <CatalogSearchIcon />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommit(value.trim());
          }}
          onBlur={() => {
            const trimmed = value.trim();
            if (trimmed !== committedValue) onCommit(trimmed);
          }}
          placeholder={placeholder}
          style={catalogSearchInputStyle}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }} />
    </div>
  );
}

function CatalogContentLoader({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 24px",
        minHeight: 220,
      }}
    >
      <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
        <span className="visually-hidden">{label}</span>
      </div>
      <div style={{ marginTop: 12, fontSize: 13, color: "#94a3b8" }}>{label}</div>
    </div>
  );
}

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  open,
  title = "Select a product",
  onProductSelect,
  onClose,
  userType,
  userId,
  signageCompanyId,
  token,
  selectedProductId,
  slotId,
  postTypeId,
  accessoryCatalogCounts,
  accessoryCategoryIds,
  accessoryCatalogLoading = false,
  accessoryRulesUserId,
  studioCustomization,
  selectedStockLines,
  baselineStockLines,
}) => {
  // Image resolution helpers (same pattern as installationpop.tsx)
  const HEX24 = /^[a-f0-9]{24}$/i;
  const ABS_URL = /^https?:\/\//i;
  const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

  /** Qty already taken by selectedProducts for each product_id + variant_id. */
  const consumedQtyByKey = useMemo(
    () => buildConsumedQtyByProductVariant(selectedStockLines),
    [selectedStockLines]
  );
  /** Edit mode: qty originally on this order (returned to Avl when removed from selection). */
  const baselineQtyByKey = useMemo(
    () => buildConsumedQtyByProductVariant(baselineStockLines),
    [baselineStockLines]
  );

  const getRemainingQty = useCallback(
    (baseAvailable: number, productId: string, variantId?: string | null) =>
      remainingAvailableQty(baseAvailable, productId, variantId, consumedQtyByKey, baselineQtyByKey),
    [consumedQtyByKey, baselineQtyByKey]
  );

  // Dynamic product state (for "search" view)
  const [products, setProducts] = useState<LinkedProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Catalog navigation state
  const [catalogView, setCatalogView] = useState<CatalogView>("search");
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  // Draft text for catalog product-table search input (type freely).
  const [catalogSearchInput, setCatalogSearchInput] = useState("");
  // Committed query used by APIs (submit on Enter only).
  const [catalogSearch, setCatalogSearch] = useState("");
  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [categorySearchInput, setCategorySearchInput] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [customizeProductId, setCustomizeProductId] = useState<string | null>(null);
  const [studioInstantiateRowId, setStudioInstantiateRowId] = useState<string | null>(null);
  const [productStyles, setProductStyles] = useState<Record<string, string>>({});
  const [productArtwork, setProductArtwork] = useState<Record<string, string>>({});
  const [productAgentNames, setProductAgentNames] = useState<Record<string, string>>({});
/** Per-row custom field values: text as `string`, pending file as `File`, uploaded file as id `string`. */
  const [rowCustomValues, setRowCustomValues] = useState<Record<string, Record<string, string | File>>>({});
  /** Local `blob:` preview URLs for pending file picks (cleared after upload or cancel). */
  const [rowCustomizationFilePreviews, setRowCustomizationFilePreviews] = useState<Record<string, Record<string, string>>>({});
  /** Row `_id` while that row's customization files are uploading (Add / table Add). */
  const [customizationPanelSubmittingRowId, setCustomizationPanelSubmittingRowId] = useState<string | null>(null);

  // Slot catalog counts from API (when opened from a slot + user clicks Browse Catalog)
  const [catalogCounts, setCatalogCounts] = useState<SlotCatalogCounts | null>(null);
  const [catalogCountsLoading, setCatalogCountsLoading] = useState(false);
  // Select2-style paginated brands (load first page on enter, more on scroll)
  const [slotBrandsItems, setSlotBrandsItems] = useState<SlotSelect2Brand[]>([]);
  const [slotBrandsPage, setSlotBrandsPage] = useState(1);
  const [slotBrandsHasMore, setSlotBrandsHasMore] = useState(false);
  const [slotBrandsLoading, setSlotBrandsLoading] = useState(false);
  const brandsScrollRef = useRef<HTMLDivElement>(null);
  const brandsSentinelRef = useRef<HTMLDivElement>(null);
  // Select2-style paginated categories
  const [slotCategoriesItems, setSlotCategoriesItems] = useState<SlotSelect2Category[]>([]);
  const [slotCategoriesPage, setSlotCategoriesPage] = useState(1);
  const [slotCategoriesHasMore, setSlotCategoriesHasMore] = useState(false);
  const [slotCategoriesLoading, setSlotCategoriesLoading] = useState(false);
  const categoriesScrollRef = useRef<HTMLDivElement>(null);
  const categoriesSentinelRef = useRef<HTMLDivElement>(null);
  // Accessory categories: paginated via accessories/categories/select2
  /** When user clicks Categories directly (allCategories), we fetch categories for the first brand; store brandId for load-more */
  const slotDirectCategoriesBrandIdRef = useRef<string | null>(null);

  // Slot catalog products (Browse Catalog → products tab when slotId is set) — from POST .../post-type-slots/{slotId}/products/datatable
  const [slotCatalogProducts, setSlotCatalogProducts] = useState<SlotProductDatatableRow[]>([]);
  const [slotCatalogProductsTotal, setSlotCatalogProductsTotal] = useState(0);
  const [slotCatalogProductsLoading, setSlotCatalogProductsLoading] = useState(false);
  const [slotProductsPagination, setSlotProductsPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [slotProductSelectedAttributes, setSlotProductSelectedAttributes] = useState<Record<string, Record<string, string>>>({});
  /** Set when Browse Catalog refetches accessory rules (kept for compatibility with existing flow). */
  const [_accessoryRuleCategoryIds, setAccessoryRuleCategoryIds] = useState<string[] | null>(null);

  // Collect hex image IDs from product rows + slot catalog products → resolve via useVariantImageUrlMap
  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of products) {
      const id = row?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    if ((slotId || (catalogView === "products" && selectedCategory) || (!slotId && postTypeId)) && (catalogView === "products" || catalogView === "allProducts") && slotCatalogProducts.length > 0) {
      for (const row of slotCatalogProducts) {
        const variants = row?.product?.variants ?? [];
        for (const v of variants) {
          for (const img of v?.images ?? []) {
            const url = (img as { url?: string })?.url;
            if (url && isHexId(url)) ids.push(String(url).trim());
          }
        }
      }
    }
    for (const it of studioCustomization?.batchLineItems ?? []) {
      const pv = String(it.preview_id ?? "").trim();
      if (pv && isHexId(pv)) ids.push(pv);
    }
    Object.values(rowCustomValues || {}).forEach((rowObj) => {
      if (!rowObj) return;
      Object.values(rowObj).forEach((val) => {
        if (typeof val === "string" && HEX24.test(val.trim())) ids.push(val.trim());
      });
    });
    return Array.from(new Set(ids));
  }, [products, slotId, postTypeId, catalogView, selectedCategory, slotCatalogProducts, studioCustomization?.batchLineItems, rowCustomValues]);

  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [{ product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
  const brandImageRows = useMemo(
    () =>
      slotBrandsItems.map((brand) => ({
        ...brand,
        image: pickRawImageValue(brand as Record<string, unknown>),
      })),
    [slotBrandsItems]
  );
  const brandImageMap = usePresignedMap(brandImageRows as Record<string, unknown>[], "image", token);
  const categoryImageRows = useMemo(
    () =>
      slotCategoriesItems.map((cat) => ({
        ...cat,
        image: pickRawImageValue(cat as Record<string, unknown>),
      })),
    [slotCategoriesItems]
  );
  const categoryImageMap = usePresignedMap(categoryImageRows as Record<string, unknown>[], "image", token);

  const mergeRowCustomValuesToUploadedStrings = useCallback(
    async (rowId: string): Promise<Record<string, string>> => {
      const raw = rowCustomValues[rowId] ?? {};
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (v instanceof File) {
          const [id] = await uploadFilesAndGetIds([v], token);
          const fid = String(id ?? "").trim();
          if (!fid) throw new Error("Upload succeeded but no file id was returned.");
          out[k] = fid;
        } else if (typeof v === "string") {
          out[k] = v;
        } else {
          out[k] = "";
        }
      }
      return out;
    },
    [rowCustomValues, token]
  );

  const handleCustomizationFilePick = useCallback((rowId: string, fieldName: string, file: File | null) => {
    setRowCustomizationFilePreviews((prev) => {
      const prevRow = prev[rowId] ?? {};
      const oldUrl = prevRow[fieldName];
      if (oldUrl && oldUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(oldUrl);
        } catch {
          /* noop */
        }
      }
      const nextRow = { ...prevRow };
      if (!file) {
        delete nextRow[fieldName];
      } else {
        nextRow[fieldName] = URL.createObjectURL(file);
      }
      const rest = { ...prev };
      if (Object.keys(nextRow).length === 0) {
        const { [rowId]: _removed, ...others } = rest;
        return others;
      }
      return { ...rest, [rowId]: nextRow };
    });
    setRowCustomValues((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] ?? {}), [fieldName]: file ?? "" },
    }));
  }, []);

  const cancelCustomizationRow = useCallback((rowId: string) => {
    setRowCustomizationFilePreviews((prev) => {
      const row = prev[rowId];
      if (row) {
        Object.values(row).forEach((url) => {
          if (typeof url === "string" && url.startsWith("blob:")) {
            try {
              URL.revokeObjectURL(url);
            } catch {
              /* noop */
            }
          }
        });
      }
      const { [rowId]: _r, ...rest } = prev;
      return rest;
    });
    setRowCustomValues((prev) => {
      const n = { ...prev };
      delete n[rowId];
      return n;
    });
    setCustomizeProductId(null);
  }, []);

  // Resolve product image URL (same logic as installationpop.tsx)
  const resolveImageUrl = useCallback((product: LinkedProduct): string => {
    const raw = product?.images?.[0]?.url;
    if (!raw) return "";
    if (ABS_URL.test(raw)) return raw;
    if (isHexId(raw)) {
      const map: Record<string, string> = (imageUrlMap as any) || {};
      return map[raw] || getImageSrc(raw, map) || "";
    }
    // legacy relative path
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    const path = raw.replace(/^\/+/, "");
    return `${base}/${encodeURI(path)}`;
  }, [imageUrlMap]);

  // Fetch dynamic products from API
  const fetchProducts = useCallback(async () => {
    if (!open || !userType || !userId || !signageCompanyId || !token) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/datatable/incoming/${userType}/${userId}/signage/${signageCompanyId}`;
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search,
          filters: {},
          sort: { field: "created_at", order: "desc" },
        }),
        signal: controller.signal,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to fetch products");
      const data: LinkedProduct[] = Array.isArray(json?.data) ? json.data : [];
      setProducts(data);
      setTotal(Number(json?.total ?? data.length));
    } catch (e: unknown) {
      if (e instanceof Error && e.name === "AbortError") return;
      setError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setLoading(false);
    }
    return () => controller.abort();
  }, [open, userType, userId, signageCompanyId, token, pagination.pageIndex, pagination.pageSize, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // When modal closes, clear slot catalog state so next open refetches
  useEffect(() => {
    if (!open) {
      setCatalogCounts(null);
      setSlotBrandsItems([]);
      setSlotBrandsHasMore(false);
      setSlotCategoriesItems([]);
      setSlotCategoriesHasMore(false);
      setSlotCatalogProducts([]);
      setSlotCatalogProductsTotal(0);
      setSlotProductsPagination({ pageIndex: 0, pageSize: 10 });
      setSlotProductSelectedAttributes({});
      slotDirectCategoriesBrandIdRef.current = null;
    }
  }, [open]);

  // Default attribute selects to first option (0th) for all products — slot, accessory, and category product tables
  useEffect(() => {
    if (slotCatalogProducts.length === 0) return;
    setSlotProductSelectedAttributes((prev) => {
      let next = { ...prev };
      for (const row of slotCatalogProducts) {
        const rowId = row._id;
        const variants = row?.product?.variants ?? [];
        const availableAttrs = buildSlotAvailableAttributes(variants);
        const current = next[rowId] ?? {};
        const defaults: Record<string, string> = {};
        for (const [attrKey, options] of Object.entries(availableAttrs)) {
          if (Array.isArray(options) && options.length > 0) {
            defaults[attrKey] = current[attrKey] ?? options[0];
          }
        }
        if (Object.keys(defaults).length > 0) {
          next[rowId] = { ...current, ...defaults };
        }
      }
      return next;
    });
  }, [slotCatalogProducts]);

  // Fetch products: by category (when category selected from Browse Catalog) or slot products (when slotId and products/allProducts)
  const fetchSlotCatalogProducts = useCallback(async () => {
    const view = catalogView;
    if (view !== "products" && view !== "allProducts") return;

    // Products by category: slot flow uses slot category products API (direct or by brand); accessory flow uses datatable
    if (view === "products" && selectedCategory && signageCompanyId && token) {
      const brandId = selectedBrand
        ? String((selectedBrand as { _id?: string; id?: string })._id ?? (selectedBrand as { _id?: string; id?: string }).id ?? "")
        : (slotDirectCategoriesBrandIdRef.current ?? "");
      const categoryId = String((selectedCategory as { _id?: string; id?: string })._id ?? (selectedCategory as { _id?: string; id?: string }).id ?? "");

      // Browse Catalog > Categories > category: POST .../slots/{slotId}/categories/{categoryId}/products/datatable (no brand)
      if (slotId && postTypeId && categoryId && !selectedBrand) {
        setSlotCatalogProductsLoading(true);
        const controller = new AbortController();
        try {
          const { results, total } = await getPostTypeSlotCategoryProductsDatatable(
            signageCompanyId,
            postTypeId,
            slotId,
            categoryId,
            token,
            {
              page: slotProductsPagination.pageIndex + 1,
              limit: slotProductsPagination.pageSize,
              search: catalogSearch || "",
              sort: { field: "created_at", order: "desc" },
            },
            controller.signal
          );
          const normalized: SlotProductDatatableRow[] = results.map((r) =>
            normalizeAccessoryBrandCategoryProductRow(r, selectedCategory.name, undefined)
          );
          setSlotCatalogProducts(normalized);
          setSlotCatalogProductsTotal(total ?? normalized.length);
        } catch (e) {
          if (e instanceof Error && e.name === "AbortError") return;
          setError(e instanceof Error ? e.message : "Unknown error");
        } finally {
          setSlotCatalogProductsLoading(false);
        }
        return () => controller.abort();
      }

      // Browse Catalog > Brand > Category: POST .../slots/.../brands/{brandId}/categories/{categoryId}/products/datatable
      if (slotId && postTypeId && brandId && categoryId) {
        setSlotCatalogProductsLoading(true);
        const controller = new AbortController();
        try {
          const { results, total } = await getPostTypeSlotBrandCategoryProductsDatatable(
            signageCompanyId,
            postTypeId,
            slotId,
            brandId,
            categoryId,
            token,
            {
              page: slotProductsPagination.pageIndex + 1,
              limit: slotProductsPagination.pageSize,
              search: catalogSearch || "",
              sort: { field: "created_at", order: "desc" },
            },
            controller.signal
          );
          const normalized: SlotProductDatatableRow[] = results.map((r) =>
            normalizeAccessoryBrandCategoryProductRow(r, selectedCategory.name, selectedBrand?.name)
          );
          setSlotCatalogProducts(normalized);
          setSlotCatalogProductsTotal(total ?? normalized.length);
        } catch (e) {
          if (e instanceof Error && e.name === "AbortError") return;
          setError(e instanceof Error ? e.message : "Unknown error");
        } finally {
          setSlotCatalogProductsLoading(false);
        }
        return () => controller.abort();
      }

      if (postTypeId && brandId && categoryId) {
        setSlotCatalogProductsLoading(true);
        const controller = new AbortController();
        try {
          const { results, total } = await getPostTypeAccessoryBrandCategoryProducts(
            signageCompanyId,
            postTypeId,
            token,
            {
              page: slotProductsPagination.pageIndex + 1,
              limit: slotProductsPagination.pageSize,
              search: catalogSearch || "",
              category_id: categoryId,
              brand_id: brandId,
              sort: { field: "created_at", order: "desc" },
            },
            controller.signal
          );
          const normalized: SlotProductDatatableRow[] = results.map((r) =>
            normalizeAccessoryBrandCategoryProductRow(r, selectedCategory.name, selectedBrand?.name)
          );
          setSlotCatalogProducts(normalized);
          setSlotCatalogProductsTotal(total ?? normalized.length);
        } catch (e) {
          if (e instanceof Error && e.name === "AbortError") return;
          setError(e instanceof Error ? e.message : "Unknown error");
        } finally {
          setSlotCatalogProductsLoading(false);
        }
        return () => controller.abort();
      }

      if (postTypeId && categoryId && !brandId) {
        setSlotCatalogProductsLoading(true);
        const controller = new AbortController();
        try {
          const { results, total } = await getPostTypeAccessoryCategoryProducts(
            signageCompanyId,
            postTypeId,
            token,
            {
              page: slotProductsPagination.pageIndex + 1,
              limit: slotProductsPagination.pageSize,
              search: catalogSearch || "",
              category_id: categoryId,
              sort: { field: "created_at", order: "desc" },
            },
            controller.signal
          );
          const normalized: SlotProductDatatableRow[] = results.map((r) =>
            normalizeAccessoryBrandCategoryProductRow(r, selectedCategory.name, selectedBrand?.name)
          );
          setSlotCatalogProducts(normalized);
          setSlotCatalogProductsTotal(total ?? normalized.length);
        } catch (e) {
          if (e instanceof Error && e.name === "AbortError") return;
          setError(e instanceof Error ? e.message : "Unknown error");
        } finally {
          setSlotCatalogProductsLoading(false);
        }
        return () => controller.abort();
      }

      setSlotCatalogProducts([]);
      setSlotCatalogProductsTotal(0);
      return;
    }

    // Slot products: when slotId is set (allProducts or products without selectedCategory)
    if (slotId && signageCompanyId && token) {
      setSlotCatalogProductsLoading(true);
      const controller = new AbortController();
      try {
        // Browse Catalog > Products: POST .../post-types/.../slots/.../products/datatable (postTypeId required). Fallback: post-type-slots datatable.
        if (postTypeId) {
          const { results, total } = await getPostTypeSlotAllProductsDatatable(
            signageCompanyId,
            postTypeId,
            slotId,
            token,
            {
              page: slotProductsPagination.pageIndex + 1,
              limit: slotProductsPagination.pageSize,
              search: catalogSearch || "",
              sort: { field: "created_at", order: "desc" },
            },
            controller.signal
          );
          const normalized: SlotProductDatatableRow[] = results.map((r) =>
            normalizeAccessoryBrandCategoryProductRow(
              r,
              r["product.category"] ?? "",
              r["product.brand"] ?? undefined
            )
          );
          setSlotCatalogProducts(normalized);
          setSlotCatalogProductsTotal(total ?? normalized.length);
        } else {
          const { results, total } = await getPostTypeSlotProductsDatatable(
            signageCompanyId,
            slotId,
            token,
            {
              page: slotProductsPagination.pageIndex + 1,
              limit: slotProductsPagination.pageSize,
              search: catalogSearch || "",
              sort: { field: "created_at", order: "desc" },
            },
            controller.signal
          );
          setSlotCatalogProducts(results);
          setSlotCatalogProductsTotal(total);
        }
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Unknown error");
      } finally {
        setSlotCatalogProductsLoading(false);
      }
      return () => controller.abort();
    }

    // Accessory products: when Product tab from Add Accessory (no slotId, but postTypeId) — POST .../post-types/{postTypeId}/products/datatable
    if (!slotId && postTypeId && signageCompanyId && token) {
      setSlotCatalogProductsLoading(true);
      const controller = new AbortController();
      try {
        const { results, total } = await getPostTypeAccessoryProductsDatatable(
          signageCompanyId,
          postTypeId,
          token,
          {
            page: slotProductsPagination.pageIndex + 1,
            limit: slotProductsPagination.pageSize,
            search: catalogSearch || "",
            sort: { field: "created_at", order: "desc" },
          },
          controller.signal
        );
        setSlotCatalogProducts(results);
        setSlotCatalogProductsTotal(total);
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Unknown error");
      } finally {
        setSlotCatalogProductsLoading(false);
      }
      return () => controller.abort();
    }
  }, [slotId, postTypeId, signageCompanyId, token, catalogView, selectedCategory?.id, selectedCategory?.name, selectedBrand?.id, selectedBrand?.name, slotProductsPagination.pageIndex, slotProductsPagination.pageSize, catalogSearch]);

  useEffect(() => {
    fetchSlotCatalogProducts();
  }, [fetchSlotCatalogProducts]);

  // Reset slot products to page 1 when search or category changes so we refetch (slot or accessory)
  useEffect(() => {
    if (catalogView === "products" || catalogView === "allProducts") {
      if (slotId || selectedCategory || (!slotId && postTypeId)) setSlotProductsPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  }, [catalogView, slotId, postTypeId, selectedCategory?.id, catalogSearch]);

  // Keep input text in sync when committed query is cleared/switched by navigation.
  useEffect(() => {
    setCatalogSearchInput(catalogSearch);
  }, [catalogSearch]);

  useEffect(() => {
    setCategorySearchInput(categorySearch);
  }, [categorySearch]);

  // Browse Catalog: slot → GET post-type-slots/{slotId}; accessory → GET accessory-rules?user_id= (fresh counts + ids)
  const handleBrowseCatalog = useCallback(async () => {
    setCatalogSearch("");
    setBrandSearchInput("");
    setBrandSearch("");
    setCategorySearchInput("");
    setCategorySearch("");
    const sid = slotId != null ? String(slotId).trim() : "";
    const vendor = signageCompanyId?.trim() ?? "";
    const ptid = postTypeId != null ? String(postTypeId).trim() : "";
    const rulesUser = accessoryRulesUserId != null ? String(accessoryRulesUserId).trim() : "";

    if (sid && vendor && token) {
      setCatalogCountsLoading(true);
      setCatalogCounts(null);
      try {
        const countsRes = await getPostTypeSlotCatalog(vendor, sid, token);
        setCatalogCounts({
          total_brands: countsRes.total_brands ?? 0,
          total_categories: countsRes.total_categories ?? 0,
          total_products: countsRes.total_products ?? 0,
        });
      } catch (err) {
        console.error("Failed to fetch slot catalog:", err);
        setCatalogCounts(null);
      } finally {
        setCatalogCountsLoading(false);
      }
    } else if (!sid && ptid && vendor && token && rulesUser) {
      setCatalogCountsLoading(true);
      setCatalogCounts(null);
      setAccessoryRuleCategoryIds(null);
      try {
        const { results } = await getAccessoryRulesForUser(vendor, ptid, rulesUser, token);
        const rule = Array.isArray(results) && results.length > 0 ? results[0] : null;
        if (!rule) {
          setCatalogCounts(null);
        } else {
          const summary = summarizeAccessoryRuleCatalog(rule);
          setAccessoryRuleCategoryIds(summary.category_ids);
          setCatalogCounts({
            total_brands: summary.total_brands,
            total_categories: summary.total_categories,
            total_products: summary.total_products,
          });
        }
      } catch (err) {
        console.error("Failed to fetch accessory rules for Browse Catalog:", err);
        setCatalogCounts(null);
      } finally {
        setCatalogCountsLoading(false);
      }
    } else if (accessoryCatalogCounts) {
      setCatalogCounts(accessoryCatalogCounts);
    } else {
      setCatalogCounts(null);
    }
    // Previous flow showed the Shop Signs landing with Brands / Categories / Products:
    // setCatalogView("picker");
    setCatalogView("brands");
  }, [slotId, signageCompanyId, token, accessoryCatalogCounts, postTypeId, accessoryRulesUserId]);

  const canLoadSlotBrands = Boolean(slotId && postTypeId && signageCompanyId && token);
  const canLoadSlotCategories = Boolean(slotId && postTypeId && signageCompanyId && token);
  const canLoadAccessoryBrands = Boolean(!slotId && postTypeId && signageCompanyId && token);
  const canLoadAccessoryCategories = Boolean(!slotId && postTypeId && signageCompanyId && token);

  const loadBrands = useCallback(
    async (q: string, page: number, append: boolean) => {
      if (!canLoadSlotBrands) return;
      setSlotBrandsLoading(true);
      try {
        const res = await getPostTypeSlotBrandsSelect2(signageCompanyId, postTypeId!, slotId!, token, {
          page,
          limit: SLOT_SELECT2_PAGE_SIZE,
          search: q,
        });
        const rawList = res?.results ?? [];
        const list: SlotSelect2Brand[] = rawList.map((b) => ({
          _id: b.id,
          name: b.name ?? b.text ?? "",
          text: b.text ?? b.name ?? "",
          product_count: b.product_count,
          logo: b.logo,
          image: b.logo,
        }));
        const hasMore = res?.pagination?.more ?? false;
        setSlotBrandsItems((prev) => (append ? [...prev, ...list] : list));
        setSlotBrandsHasMore(hasMore);
        setSlotBrandsPage(page);
      } catch {
        setSlotBrandsItems((prev) => (append ? prev : []));
        setSlotBrandsHasMore(false);
      } finally {
        setSlotBrandsLoading(false);
      }
    },
    [canLoadSlotBrands, signageCompanyId, postTypeId, slotId, token]
  );

  const loadCategories = useCallback(
    async (q: string, page: number, append: boolean) => {
      if (!canLoadSlotCategories) return;
      setSlotCategoriesLoading(true);
      try {
        const res = await getPostTypeSlotSelect2(signageCompanyId, postTypeId!, slotId!, token, {
          page,
          limit: SLOT_SELECT2_PAGE_SIZE,
          search: q,
          type: "categories",
        });
        const list = res?.results?.[0]?.categories ?? [];
        const hasMore = res?.pagination?.more ?? false;
        setSlotCategoriesItems((prev) => (append ? [...prev, ...list] : list));
        setSlotCategoriesHasMore(hasMore);
        setSlotCategoriesPage(page);
      } catch {
        setSlotCategoriesItems((prev) => (append ? prev : []));
        setSlotCategoriesHasMore(false);
      } finally {
        setSlotCategoriesLoading(false);
      }
    },
    [canLoadSlotCategories, signageCompanyId, postTypeId, slotId, token]
  );

  /** When user clicks Categories directly (allCategories): fetch slot categories via .../slots/{slotId}/categories/select2 */
  const loadDirectSlotCategories = useCallback(
    async (q: string, page: number, append: boolean) => {
      if (!slotId || !postTypeId || !signageCompanyId || !token) return;
      setSlotCategoriesLoading(true);
      try {
        const res = await getPostTypeSlotCategoriesSelect2(signageCompanyId, postTypeId, slotId, token, {
          page,
          limit: SLOT_SELECT2_PAGE_SIZE,
          search: q,
        });
        const rawList = res?.results ?? [];
        const list: SlotSelect2Category[] = rawList.map((c) => ({
          _id: c.id,
          name: c.name ?? c.text ?? "",
          text: c.text ?? c.name ?? "",
          product_count: c.count,
          image: c.image,
        }));
        setSlotCategoriesItems((prev) => (append ? [...prev, ...list] : list));
        setSlotCategoriesHasMore(res?.pagination?.more ?? false);
        setSlotCategoriesPage(page);
      } catch {
        setSlotCategoriesItems((prev) => (append ? prev : []));
        setSlotCategoriesHasMore(false);
      } finally {
        setSlotCategoriesLoading(false);
      }
    },
    [signageCompanyId, postTypeId, slotId, token]
  );

  /** Slot + selected brand: slot brand-categories API. No slot + postTypeId: accessory brand categories API. */
  const loadCategoriesByBrand = useCallback(
    async (brandId: string, q: string, page: number, append: boolean) => {
      if (!signageCompanyId || !token || !brandId) return;
      setSlotCategoriesLoading(true);
      try {
        if (slotId) {
          const res = await getPostTypeSlotBrandCategoriesSelect2(
            signageCompanyId,
            postTypeId!,
            slotId,
            brandId,
            token,
            { page, limit: SLOT_SELECT2_PAGE_SIZE, search: q }
          );
          const rawList = res?.results ?? [];
          const list: SlotSelect2Category[] = rawList.map((c) => ({
            _id: c.id,
            name: c.name ?? c.text ?? "",
            text: c.text ?? c.name ?? "",
            product_count: c.count,
            image: c.image,
          }));
          setSlotCategoriesItems((prev) => (append ? [...prev, ...list] : list));
          setSlotCategoriesHasMore(res?.pagination?.more ?? false);
          setSlotCategoriesPage(page);
        } else if (postTypeId) {
          const { data, hasMore } = await getPostTypeAccessoriesBrandCategoriesSelect2(
            signageCompanyId,
            postTypeId,
            brandId,
            token,
            { page, limit: SLOT_SELECT2_PAGE_SIZE, search: q }
          );
          const list: SlotSelect2Category[] = data.map((c) => ({
            _id: c.id,
            name: c.text,
            text: c.text,
            product_count: c.count,
            image: (c as { image?: string }).image,
          }));
          setSlotCategoriesItems((prev) => (append ? [...prev, ...list] : list));
          setSlotCategoriesHasMore(hasMore);
          setSlotCategoriesPage(page);
        } else {
          setSlotCategoriesItems((prev) => (append ? prev : []));
          setSlotCategoriesHasMore(false);
          setSlotCategoriesPage(page);
        }
      } catch {
        setSlotCategoriesItems((prev) => (append ? prev : []));
        setSlotCategoriesHasMore(false);
      } finally {
        setSlotCategoriesLoading(false);
      }
    },
    [signageCompanyId, token, slotId, postTypeId]
  );

  const loadAccessoryBrands = useCallback(async (q: string, page: number, append: boolean) => {
    if (!canLoadAccessoryBrands || !postTypeId) return;
    setSlotBrandsLoading(true);
    try {
      const res = await getPostTypeAccessoriesBrandsSelect2(
        signageCompanyId,
        postTypeId,
        token,
        { page, limit: SLOT_SELECT2_PAGE_SIZE, search: q }
      );
      const list: SlotSelect2Brand[] = (res.results ?? []).map((b) => ({
        _id: String(b.id ?? b._id ?? ""),
        name: b.name ?? b.text ?? "",
        text: b.text ?? b.name ?? "",
        product_count:
          typeof b.category_count === "number"
            ? b.category_count
            : typeof b.product_count === "number"
              ? b.product_count
              : undefined,
        categories: b.categories,
        logo: b.logo,
        image: b.logo ?? b.image,
      }));
      setSlotBrandsItems((prev) => (append ? [...prev, ...list] : list));
      setSlotBrandsHasMore(res?.pagination?.more ?? false);
      setSlotBrandsPage(page);
    } catch {
      setSlotBrandsItems((prev) => (append ? prev : []));
      setSlotBrandsHasMore(false);
    } finally {
      setSlotBrandsLoading(false);
    }
  }, [canLoadAccessoryBrands, signageCompanyId, postTypeId, token]);

  const loadAccessoryCategories = useCallback(async (q: string, page: number, append: boolean) => {
    if (!canLoadAccessoryCategories || !postTypeId) return;
    setSlotCategoriesLoading(true);
    try {
      const res = await getPostTypeAccessoriesCategoriesSelect2(
        signageCompanyId,
        postTypeId,
        token,
        { page, limit: SLOT_SELECT2_PAGE_SIZE, search: q }
      );
      const list: SlotSelect2Category[] = (res.results ?? []).map((c) => ({
        _id: String(c.id ?? ""),
        name: c.text ?? c.name ?? "",
        text: c.text ?? c.name ?? "",
        product_count: typeof c.count === "number" ? c.count : undefined,
        image: c.image,
      }));
      setSlotCategoriesItems((prev) => (append ? [...prev, ...list] : list));
      setSlotCategoriesHasMore(res?.pagination?.more ?? false);
      setSlotCategoriesPage(page);
    } catch {
      setSlotCategoriesItems((prev) => (append ? prev : []));
      setSlotCategoriesHasMore(false);
    } finally {
      setSlotCategoriesLoading(false);
    }
  }, [canLoadAccessoryCategories, signageCompanyId, postTypeId, token]);

  // Load first page when entering brands view (slot: select2; accessory: bulk-by-ids)
  useEffect(() => {
    if (!open || catalogView !== "brands") return;
    if (canLoadSlotBrands) loadBrands(brandSearch, 1, false);
    else if (canLoadAccessoryBrands) loadAccessoryBrands(brandSearch, 1, false);
  }, [open, catalogView, canLoadSlotBrands, canLoadAccessoryBrands, brandSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load first page when entering allCategories or categories view (slot or accessory)
  useEffect(() => {
    if (!open || (catalogView !== "allCategories" && catalogView !== "categories")) return;
    const brandId = selectedBrand?.id ?? (selectedBrand as { _id?: string })?._id;
    if (catalogView === "categories" && selectedBrand && brandId) {
      loadCategoriesByBrand(brandId, categorySearch, 1, false);
    } else if (catalogView === "allCategories" && canLoadSlotCategories) {
      loadDirectSlotCategories(categorySearch, 1, false);
    } else if (catalogView === "allCategories" && canLoadAccessoryCategories) {
      loadAccessoryCategories(categorySearch, 1, false);
    }
  }, [open, catalogView, canLoadSlotCategories, canLoadAccessoryCategories, slotId, selectedBrand?.id, (selectedBrand as { _id?: string })?._id, categorySearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to load more brands (slot/accessory: next API page)
  useEffect(() => {
    const useDynamicBrands = canLoadSlotBrands || canLoadAccessoryBrands;
    if (!useDynamicBrands || !slotBrandsHasMore || slotBrandsLoading) return;
    const el = brandsSentinelRef.current;
    const root = brandsScrollRef.current;
    if (!el || !root) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (canLoadSlotBrands) loadBrands(brandSearch, slotBrandsPage + 1, true);
        else if (canLoadAccessoryBrands) loadAccessoryBrands(brandSearch, slotBrandsPage + 1, true);
      },
      { root, rootMargin: "100px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [canLoadSlotBrands, canLoadAccessoryBrands, slotBrandsHasMore, slotBrandsLoading, slotBrandsPage, brandSearch, loadBrands, loadAccessoryBrands]);

  // Scroll to load more categories (slot & accessory: by-brand API when a brand is selected; allCategories: slot/accessory next API page)
  const useDynamicCategoriesByBrand = Boolean(selectedBrand && catalogView === "categories");
  useEffect(() => {
    const useDynamicCategories =
      useDynamicCategoriesByBrand || (catalogView === "allCategories" && canLoadSlotCategories) || canLoadAccessoryCategories;
    if (!useDynamicCategories || !slotCategoriesHasMore || slotCategoriesLoading) return;
    const el = categoriesSentinelRef.current;
    const root = categoriesScrollRef.current;
    if (!el || !root) return;
    const brandId = selectedBrand?.id ?? (selectedBrand as { _id?: string })?._id;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (useDynamicCategoriesByBrand && selectedBrand && brandId) {
          loadCategoriesByBrand(brandId, categorySearch, slotCategoriesPage + 1, true);
        } else if (catalogView === "allCategories" && canLoadSlotCategories) {
          loadDirectSlotCategories(categorySearch, slotCategoriesPage + 1, true);
        } else if (canLoadAccessoryCategories) {
          loadAccessoryCategories(categorySearch, slotCategoriesPage + 1, true);
        }
      },
      { root, rootMargin: "100px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [useDynamicCategoriesByBrand, canLoadSlotCategories, canLoadAccessoryCategories, slotCategoriesHasMore, slotCategoriesLoading, slotCategoriesPage, selectedBrand?.id, (selectedBrand as { _id?: string })?._id, categorySearch, catalogView, loadCategoriesByBrand, loadDirectSlotCategories, loadAccessoryCategories]);

  // useReactTable instance — drives PaginationControls + triggers API on page/pageSize change
  const table = useReactTable({
    data: products,
    columns: [],
    pageCount: Math.max(1, Math.ceil(total / pagination.pageSize)),
    state: { pagination },
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchInput(""); setSearch(""); setPagination({ pageIndex: 0, pageSize: 10 }); setError(null);
      setCatalogView("search"); setSelectedBrand(null); setSelectedCategory(null);
      setCatalogSearch(""); setBrandSearchInput(""); setBrandSearch(""); setCategorySearchInput(""); setCategorySearch(""); setCustomizeProductId(null); setRowCustomValues({});
      setCustomizationPanelSubmittingRowId(null);
      setRowCustomizationFilePreviews((prev) => {
        Object.values(prev).forEach((row) => {
          Object.values(row || {}).forEach((url) => {
            if (typeof url === "string" && url.startsWith("blob:")) {
              try {
                URL.revokeObjectURL(url);
              } catch {
                /* noop */
              }
            }
          });
        });
        return {};
      });
    }
  }, [open]);

  const noUser = !userType || !userId;

  const handleSelectDynamic = (product: LinkedProduct) => {
    onProductSelect(product);
    onClose();
  };

  const resetAndClose = () => {
    setRowCustomizationFilePreviews((prev) => {
      Object.values(prev).forEach((row) => {
        Object.values(row || {}).forEach((url) => {
          if (typeof url === "string" && url.startsWith("blob:")) {
            try {
              URL.revokeObjectURL(url);
            } catch {
              /* noop */
            }
          }
        });
      });
      return {};
    });
    setCatalogView("search"); setSelectedBrand(null); setSelectedCategory(null);
    setCatalogSearch(""); setBrandSearchInput(""); setBrandSearch(""); setCategorySearchInput(""); setCategorySearch(""); setCustomizeProductId(null); setRowCustomValues({});
    setCustomizationPanelSubmittingRowId(null);
    onClose();
  };

  // Catalog back button logic
  const handleBack = () => {
    if (catalogView === "products" && selectedBrand) { setCatalogView("categories"); setSelectedCategory(null); setCatalogSearch(""); }
    else if (catalogView === "products" && !selectedBrand) { setCatalogView("allCategories"); setSelectedCategory(null); setCatalogSearch(""); }
    else if (catalogView === "categories") { setCatalogView("brands"); setSelectedBrand(null); setCategorySearchInput(""); setCategorySearch(""); }
    else if (catalogView === "brands") {
      // Previous Shop Signs flow returned to the Brands / Categories / Products picker:
      // setCatalogView("picker");
      setCatalogView("search");
      setCatalogSearch("");
    }
    else if (catalogView === "allCategories" || catalogView === "allProducts") { setCatalogView("picker"); setCatalogSearch(""); }
    else { setCatalogView("search"); setCatalogSearch(""); }
  };

  // Filtered catalog products
  const filteredCatalogProducts = useMemo(() => {
    return CATALOG_PRODUCTS.filter((p) => {
      const matchesSearch = p.name.toLowerCase().includes(catalogSearch.toLowerCase()) || p.sku.toLowerCase().includes(catalogSearch.toLowerCase());
      const matchesBrand = selectedBrand ? p.brand === selectedBrand.name : true;
      const matchesCategory = (catalogView === "products" || catalogView === "allProducts") && selectedCategory ? p.category === selectedCategory.name : true;
      return matchesSearch && matchesBrand && matchesCategory;
    });
  }, [catalogSearch, selectedBrand, selectedCategory, catalogView]);

  const shopSignsLabel = "Shop Signs";
  const inventoryTitle = useMemo(() => {
    const rawTitle = typeof title === "string" ? title.trim() : "";
    if (!rawTitle) return title;
    return /from your inventory/i.test(rawTitle) ? rawTitle : `${rawTitle} from Your Inventory`;
  }, [title]);

  // Modal title based on catalog view
  const modalTitle = catalogView === "picker" ? shopSignsLabel
    : catalogView === "brands" ? "Select a Brand"
    : catalogView === "allCategories" ? "Select a Category"
    : catalogView === "allProducts" ? "All Products"
    : catalogView === "categories" && selectedBrand ? `${selectedBrand.name} — Categories`
    : catalogView === "products" && selectedCategory ? selectedCategory.name
    : inventoryTitle;

  // Shared render helpers for desktop table + mobile cards
  const renderProductImage = (product: LinkedProduct) => {
    const imgUrl = resolveImageUrl(product);
    return imgUrl ? (
      <img src={imgUrl} alt={product.name} style={{ width: 75, height: 75, objectFit: "contain", borderRadius: 6, border: "1px solid #e2e8f0", background: "#f1f5f9" }} />
    ) : (
      <div style={{ width: 75, height: 75, borderRadius: 6, background: "#f8fafc", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#94a3b8", textAlign: "center", lineHeight: 1.2 }}>No<br />Image</div>
    );
  };

  const openCustomizeInStudio = useCallback(
    async (
      templateId: string,
      rowId: string,
      productId: string,
      variantId: string
    ) => {
      const studioBaseUrl = (process.env.REACT_APP_STUDIO_BASE_URL || "").trim().replace(/\/+$/, "");
      const vendorId = String(signageCompanyId ?? "").trim();
      const tid = String(templateId ?? "").trim();
      if (!tid || !studioBaseUrl || !vendorId) {
        showError("Cannot open Studio: missing template, studio URL, or signage company.");
        return;
      }
      try {
        setStudioInstantiateRowId(rowId);

        if (studioCustomization?.source) {
          await studioCustomization.onBeforeStudioRedirect?.();
          const instantiateResult = await instantiateDesignFromTemplate(tid, vendorId);
          const designId = String(instantiateResult.design_id ?? "").trim();
          if (!designId) {
            throw new Error("Design id not returned from instantiate API");
          }
          const existingBatchId = String(studioCustomization.existingBatchId ?? "").trim();
          const baseReq = {
            source: studioCustomization.source,
            product_id: String(productId ?? "").trim(),
            variant_id: String(variantId ?? "").trim(),
            template_id: designId,
            line_item_key: String(rowId ?? "").trim(),
            mode: "create" as const,
            ...(existingBatchId ? { batch_id: existingBatchId } : {}),
          };
          let batchId: string;
          let contextId: string;
          if (studioCustomization.source === "signage_create_order") {
            const orderSnap = (await studioCustomization.getOrderSnapshot?.()) ?? {};
            const res = await createCustomizationContext({
              ...baseReq,
              order: orderSnap as Record<string, unknown>,
            });
            batchId = res.batch_id;
            contextId = res.context_id;
          } else if (studioCustomization.source === "signage_add_installation") {
            const installationSnap = (await studioCustomization.getInstallationOrderSnapshot?.()) ?? {};
            const res = await createCustomizationContext({
              ...baseReq,
              installation_order: installationSnap as Record<string, unknown>,
            });
            batchId = res.batch_id;
            contextId = res.context_id;
          } else {
            const res = await createCustomizationContext(baseReq);
            batchId = res.batch_id;
            contextId = res.context_id;
          }
          const rawReturn = `${window.location.pathname}${window.location.search || ""}`;
          const _u = new URL(rawReturn, window.location.origin);
          _u.searchParams.delete("batch_id");
          _u.searchParams.delete("context_id");
          const returnPath = `${_u.pathname}${_u.search || ""}`;
          const studioUrl = new URL(`${studioBaseUrl}/open-design/${encodeURIComponent(designId)}`);
          studioUrl.searchParams.set("batch_id", batchId);
          studioUrl.searchParams.set("context_id", contextId);
          studioUrl.searchParams.set("return_path", returnPath);
          window.location.assign(studioUrl.toString());
          return;
        }

        const result = await instantiateDesignFromTemplate(tid, vendorId);
        // Build a return path without any existing batch/context params so Studio's
        // return_path does not keep old batch_id/context_id values.
        const rawReturn = `${window.location.pathname}${window.location.search || ""}`;
        const _u = new URL(rawReturn, window.location.origin);
        _u.searchParams.delete("batch_id");
        _u.searchParams.delete("context_id");
        const returnPath = `${_u.pathname}${_u.search || ""}`;
        const studioUrl = new URL(
          `${studioBaseUrl}/open-design/${encodeURIComponent(result.design_id)}`
        );
        studioUrl.searchParams.set("return_path", returnPath);
        window.location.assign(studioUrl.toString());
      } catch (e) {
        console.error(e);
        showError(e instanceof Error ? e.message : "Failed to open Studio");
      } finally {
        setStudioInstantiateRowId(null);
      }
    },
    [signageCompanyId, studioCustomization]
  );

  /** Re-open Studio for a line that already has design + context in the active batch (no new instantiate). */
  const openEditDesignInStudio = useCallback(
    async (rowId: string, batchMatch: StudioBatchLineItem) => {
      const studioBaseUrl = (process.env.REACT_APP_STUDIO_BASE_URL || "").trim().replace(/\/+$/, "");
      const designId = String(batchMatch.design_id ?? "").trim();
      const contextId = String(batchMatch.context_id ?? "").trim();
      const batchId = String(studioCustomization?.existingBatchId ?? "").trim();
      if (!designId || !studioBaseUrl || !batchId || !contextId) {
        showError("Cannot open Studio: missing design, batch, or context for this line.");
        return;
      }
      try {
        setStudioInstantiateRowId(rowId);
        await studioCustomization?.onBeforeStudioRedirect?.();
        const rawReturn = `${window.location.pathname}${window.location.search || ""}`;
        const _u = new URL(rawReturn, window.location.origin);
        _u.searchParams.delete("batch_id");
        _u.searchParams.delete("context_id");
        const returnPath = `${_u.pathname}${_u.search || ""}`;
        const studioUrl = new URL(`${studioBaseUrl}/open-design/${encodeURIComponent(designId)}`);
        studioUrl.searchParams.set("batch_id", batchId);
        studioUrl.searchParams.set("context_id", contextId);
        studioUrl.searchParams.set("return_path", returnPath);
        window.location.assign(studioUrl.toString());
      } catch (e) {
        console.error(e);
        showError(e instanceof Error ? e.message : "Failed to open Studio");
      } finally {
        setStudioInstantiateRowId(null);
      }
    },
    [studioCustomization]
  );

  const renderActionButton = (
    product: LinkedProduct,
    isSelected: boolean,
    baseAvailable: number,
    remainingQty: number
  ) => {
    // Your Inventory / linked / own: never show Buy — disable Add when out of stock.
    if (baseAvailable <= 0 || remainingQty <= 0) {
      return (
        <button
          type="button"
          disabled
          style={{ background: "#94a3b8", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 14, fontWeight: 600, cursor: "not-allowed", opacity: 0.7, width: "100%" }}
        >Add</button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => handleSelectDynamic(product)}
        style={{ background: isSelected ? "#22c55e" : "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.15s", width: "100%" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = isSelected ? "#16a34a" : "#1d4ed8"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "#22c55e" : "#2563eb"; }}
      >{isSelected ? "✓ Added" : "Add"}</button>
    );
  };

  // Slot products table (accordion customize row like ordercheckoutpop: thead + tbody with inline panel row + PaginationControls)
  const slotProductColumns = useMemo<ColumnDef<SlotProductDatatableRow, unknown>[]>(() => [
    slotProductColumnHelper.display({
      id: "image",
      header: () => <div className="table-headers text-center">Image</div>,
      cell: ({ row }) => {
        const r = row.original;
        const variants = r?.product?.variants ?? [];
        const selectedAttrs = slotProductSelectedAttributes[r._id] ?? {};
        const selectedVariant = findSlotVariantByAttributes(variants, selectedAttrs) ?? variants[0];
        const vid = String((selectedVariant as { variant_id?: string })?.variant_id ?? "").trim();
        const batchMatch =
          studioCustomization?.batchLineItems && selectedVariant
            ? findStudioBatchLineItem(studioCustomization.batchLineItems, r._id, vid)
            : undefined;
        const linked = selectedVariant ? slotRowToLinkedProduct(r, selectedVariant, batchMatch) : null;
        const imgUrl = linked ? resolveImageUrl(linked) : "";
        return (
          <div className="table-cells text-center">
            {imgUrl ? (
              <img src={imgUrl} alt="" style={{ width: 72, height: 72, objectFit: "contain", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f1f5f9" }} />
            ) : (
              <div style={{ width: 72, height: 72, borderRadius: 8, background: "#f8fafc", border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#94a3b8", textAlign: "center", lineHeight: 1.2 }}>No<br />Image</div>
            )}
          </div>
        );
      },
    }),
    slotProductColumnHelper.display({
      id: "name",
      header: () => <div className="table-headers">Product Name</div>,
      cell: ({ row }) => {
        const r = row.original;
        const variants = r?.product?.variants ?? [];
        const selectedAttrs = slotProductSelectedAttributes[r._id] ?? {};
        const selectedVariant = findSlotVariantByAttributes(variants, selectedAttrs) ?? variants[0];
        const name = (selectedVariant as { variant_name?: string })?.variant_name ?? r.product?.category ?? "Product";
        const sku = (selectedVariant as { sku?: string })?.sku ?? "—";
        return (
          <div className="table-cells table-cells-product-name" title={name}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",fontWeight: 600, fontSize: 16,marginBottom: 7 }}>{name}</div>
            <div style={{ fontSize: 14, color: "#5b5c5e", marginTop: 6, fontWeight: 600 }}>SKU: {sku}</div>
          </div>
        );
      },
    }),
    slotProductColumnHelper.display({
      id: "category",
      header: () => <div className="table-headers">Category</div>,
      cell: ({ row }) => {
        const value = row.original?.product?.category ?? "—";
        // const cs = getCatStyle(value);
        return (
          <div className="table-cells">
            <span style={{ display: "inline-block", padding: "6px 12px", borderRadius: 6, fontSize: 14, fontWeight: 600, background: "#dcfce7", color: "#16a34a" }}>{value}</span>
          </div>
        );
      },
    }),
    slotProductColumnHelper.display({
      id: "avl_qty",
      header: () => <div className="table-headers text-center">Avl Qty</div>,
      cell: ({ row }) => {
        const r = row.original;
        const variants = r?.product?.variants ?? [];
        const selectedAttrs = slotProductSelectedAttributes[r._id] ?? {};
        const selectedVariant = findSlotVariantByAttributes(variants, selectedAttrs) ?? variants[0];
        const invList = selectedVariant && (selectedVariant as { inventory?: { available?: number }[] }).inventory;
        const baseQty = invList ? invList.reduce((s, inv) => s + (Number(inv.available) || 0), 0) : 0;
        const vid = String((selectedVariant as { variant_id?: string })?.variant_id ?? "").trim();
        const qty = getRemainingQty(baseQty, r._id, vid);
        return (
          <div className="table-cells text-center">
            <span style={{ fontWeight: 600, color: qty > 0 ? "#334155" : "#ef4444",fontSize: 14 }}>{qty.toLocaleString()}</span>
          </div>
        );
      },
    }),
    slotProductColumnHelper.display({
      id: "attributes",
      header: () => <div className="table-headers text-center">Attributes</div>,
      cell: ({ row }) => {
        const r = row.original;
        const rowId = r._id;
        const variants = r?.product?.variants ?? [];
        const availableAttrs = buildSlotAvailableAttributes(variants);
        const selectedAttrs = slotProductSelectedAttributes[rowId] ?? {};
        if (Object.keys(availableAttrs).length === 0) return <div className="table-cells text-center"><span className="text-muted">—</span></div>;
        return (
          <div className="table-cells">
            <div className="psm-attr-list attr-titlecase" style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
              {Object.entries(availableAttrs).map(([attrKey, options]) => {
                const value = selectedAttrs[attrKey] ?? "";
                return (
                  <div key={attrKey}>
                    <div className="psm-attr-label ins-attr-label">{String(attrKey || "").replace(/_/g, " ")}</div>
                    <select
                      name={`${rowId}-${attrKey}`}
                      className="psm-native-attr-select attr-select"
                      value={value}
                      onChange={(e) =>
                        setSlotProductSelectedAttributes((prev) => ({
                          ...prev,
                          [rowId]: { ...(prev[rowId] ?? {}), [attrKey]: e.target.value },
                        }))
                      }
                    >
                      <option value="">- Select -</option>
                      {(options || []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        );
      },
    }),
    slotProductColumnHelper.display({
      id: "custom",
      header: () => <div className="table-headers text-center">Customize</div>,
      cell: ({ row }) => {
        const r = row.original;
        const rowId = r._id;
        const variants = r?.product?.variants ?? [];
        const hasCustomize = slotProductHasCustomization(variants);
        const selectedAttrs = slotProductSelectedAttributes[rowId] ?? {};
        const selectedVariant = findSlotVariantByAttributes(variants, selectedAttrs) ?? variants[0];
        const customizationFields = getSlotCustomizationFields(selectedVariant, variants);
        const hasRequiredCustomizationField = customizationFields.some((field) => Boolean(field?.required));
        const templateIdForStudio = (selectedVariant as { template_id?: string } | undefined)?.template_id;
        const hasStudioTemplate = variantHasStudioTemplateId(selectedVariant);
        const vidForBatch = String((selectedVariant as { variant_id?: string })?.variant_id ?? "").trim();
        const batchMatch =
          studioCustomization?.batchLineItems && selectedVariant
            ? findStudioBatchLineItem(studioCustomization.batchLineItems, r._id, vidForBatch)
            : undefined;
        const canEditExistingDesign =
          !!studioCustomization?.existingBatchId &&
          !!batchMatch?.design_id &&
          !!String(batchMatch.context_id ?? "").trim();
        const showCustomizeColumn = hasCustomize || hasStudioTemplate;
        const isOpen = customizeProductId === rowId;
        return (
          <div className="table-cells text-center">
            {!showCustomizeColumn ? (
              <span className="text-muted">—</span>
            ) : (
              <div style={{ display: "inline-flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                {hasCustomize && (
                  <div style={{ position: "relative", display: "inline-flex" }}>
                    {hasRequiredCustomizationField ? (
                      <span
                        style={{
                          position: "absolute",
                          top: 10,
                          left: -15,
                          color: "#dc2626",
                          fontSize: 18,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        *
                      </span>
                    ) : null}
                    <button type="button" onClick={() => setCustomizeProductId(isOpen ? null : rowId)}
                      style={{ background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 15, fontWeight: 600, color: isOpen ? "#2563eb" : "#6b7280", padding: "6px 12px", transition: "all 0.2s" }}>
                      {isOpen ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                      )}
                      <span>Customize</span>
                    </button>
                  </div>
                )}
                {hasStudioTemplate && (
                  <button
                    type="button"
                    disabled={studioInstantiateRowId === rowId || !signageCompanyId}
                    onClick={() => {
                      const tid = String(templateIdForStudio ?? "").trim();
                      const pid = String(r._id ?? "").trim();
                      const vid = String((selectedVariant as { variant_id?: string })?.variant_id ?? "").trim();
                      if (canEditExistingDesign && batchMatch) {
                        void openEditDesignInStudio(rowId, batchMatch);
                        return;
                      }
                      if (tid) openCustomizeInStudio(tid, rowId, pid, vid);
                    }}
                    style={{
                      background: "#eef2ff",
                      border: "1px solid #c7d2fe",
                      borderRadius: 8,
                      cursor: studioInstantiateRowId === rowId ? "wait" : "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#3730a3",
                      padding: "5px 10px",
                      transition: "all 0.2s",
                      opacity: studioInstantiateRowId === rowId ? 0.75 : 1,
                    }}
                  >
                    {studioInstantiateRowId === rowId
                      ? "Opening…"
                      : canEditExistingDesign
                        ? "Edit design"
                        : "Customize in studio"}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      },
    }),
    slotProductColumnHelper.display({
      id: "actions",
      header: () => <div className="table-headers text-center">Actions</div>,
      cell: ({ row }) => {
        const r = row.original;
        const variants = r?.product?.variants ?? [];
        const selectedAttrs = slotProductSelectedAttributes[r._id] ?? {};
        const selectedVariant = findSlotVariantByAttributes(variants, selectedAttrs) ?? variants[0];
        const customizationFields = getSlotCustomizationFields(selectedVariant, variants);
        const requiredCustomizationFields = customizationFields.filter((field) => Boolean(field?.required));
        const customValues = rowCustomValues[r._id] ?? {};
        const hasRequiredCustomizationField = requiredCustomizationFields.length > 0;
        const hasRequiredCustomizationValues = requiredCustomizationFields.every((field) =>
          fieldCustomizationValueSatisfied(field, customValues[field.field_name])
        );
        const hasCustomize = slotProductHasCustomization(variants);
        const canSubmitThisRow = hasCustomize
          ? (!hasRequiredCustomizationField || (customizeProductId === r._id && hasRequiredCustomizationValues))
          : true;
        const invList = selectedVariant && (selectedVariant as { inventory?: { available?: number }[] }).inventory;
        const baseAvailableQty = invList ? invList.reduce((s, inv) => s + (Number(inv.available) || 0), 0) : 0;
        const vidForAdd = String((selectedVariant as { variant_id?: string })?.variant_id ?? "").trim();
        const remainingQty = getRemainingQty(baseAvailableQty, r._id, vidForAdd);
        const inStock = baseAvailableQty > 0;
        const canAddByStock = remainingQty > 0;
        const batchMatchForAdd =
          studioCustomization?.batchLineItems && selectedVariant
            ? findStudioBatchLineItem(studioCustomization.batchLineItems, r._id, vidForAdd)
            : undefined;
        return (
          <div className="table-cells text-center">
            {inStock && selectedVariant ? (
              <button
                type="button"
                disabled={!canSubmitThisRow || !canAddByStock || customizationPanelSubmittingRowId === r._id}
                onClick={async () => {
                  if (customizationPanelSubmittingRowId || !canAddByStock) return;
                  setCustomizationPanelSubmittingRowId(r._id);
                  try {
                    const merged = await mergeRowCustomValuesToUploadedStrings(r._id);
                    const linked = slotRowToLinkedProduct(r, selectedVariant, batchMatchForAdd, merged);
                    handleSelectDynamic(linked);
                  } catch (err) {
                    showError(err instanceof Error ? err.message : "File upload failed");
                  } finally {
                    setCustomizationPanelSubmittingRowId(null);
                  }
                }}
                style={{ background: canAddByStock ? "#2563eb" : "#94a3b8", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 15, fontWeight: 600, cursor: canSubmitThisRow && canAddByStock && customizationPanelSubmittingRowId !== r._id ? "pointer" : "not-allowed", opacity: canSubmitThisRow && canAddByStock && customizationPanelSubmittingRowId !== r._id ? 1 : 0.6, width: "100%" }}
              >
                {customizationPanelSubmittingRowId === r._id ? "Adding…" : "Add"}
              </button>
            ) : selectedVariant ? (
              <button
                type="button"
                disabled={!canSubmitThisRow || customizationPanelSubmittingRowId === r._id}
                onClick={async () => {
                  // Shop Signs out-of-stock Buy: still add to line item + assign to slot
                  if (customizationPanelSubmittingRowId) return;
                  setCustomizationPanelSubmittingRowId(r._id);
                  try {
                    const merged = await mergeRowCustomValuesToUploadedStrings(r._id);
                    const linked = slotRowToLinkedProduct(r, selectedVariant, batchMatchForAdd, merged);
                    handleSelectDynamic(linked);
                  } catch (err) {
                    showError(err instanceof Error ? err.message : "File upload failed");
                  } finally {
                    setCustomizationPanelSubmittingRowId(null);
                  }
                }}
                style={{ background: "#fff", color: "#f59e0b", border: "2px solid #f59e0b", borderRadius: 6, padding: "6px 14px", fontSize: 15, fontWeight: 600, cursor: canSubmitThisRow && customizationPanelSubmittingRowId !== r._id ? "pointer" : "not-allowed", opacity: canSubmitThisRow && customizationPanelSubmittingRowId !== r._id ? 1 : 0.6, width: "100%" }}
              >
                {customizationPanelSubmittingRowId === r._id ? "Adding…" : "Buy"}
              </button>
            ) : (
              <button
                type="button"
                disabled
                style={{ background: "#94a3b8", color: "#fff", border: "none", borderRadius: 6, padding: "7px 16px", fontSize: 15, fontWeight: 600, cursor: "not-allowed", opacity: 0.7, width: "100%" }}
              >
                Add
              </button>
            )}
          </div>
        );
      },
    }),
  ], [
    slotProductSelectedAttributes,
    customizeProductId,
    rowCustomValues,
    customizationPanelSubmittingRowId,
    mergeRowCustomValuesToUploadedStrings,
    resolveImageUrl,
    signageCompanyId,
    studioInstantiateRowId,
    openCustomizeInStudio,
    openEditDesignInStudio,
    studioCustomization,
    getRemainingQty,
  ]);

  const slotProductsTable = useReactTable({
    data: slotCatalogProducts,
    columns: slotProductColumns,
    getRowId: (row) => row._id,
    pageCount: Math.max(1, Math.ceil(slotCatalogProductsTotal / slotProductsPagination.pageSize)),
    state: { pagination: slotProductsPagination },
    manualPagination: true,
    onPaginationChange: (updater) => {
      setSlotProductsPagination((prev) => {
        const next = typeof updater === "function" ? updater(prev) : prev;
        if (next.pageSize !== prev.pageSize) return { ...next, pageIndex: 0 };
        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
  });

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="psm-modal-overlay"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15, 23, 42, 0.5)", backdropFilter: "blur(4px)",
        display: "flex", zIndex: 10000,
      }}
      onClick={resetAndClose}
    >
      <div
        className="psm-modal-content"
        style={{
          background: "#fff", borderRadius: 16,
          maxHeight: "108vh", display: "flex", flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.18), 0 8px 24px rgba(0,0,0,0.08)",
          overflow: "hidden", border: "1px solid rgba(255,255,255,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ═══ HEADER ═══ */}
        <div style={{ padding: "20px 24px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {catalogView !== "search" && (
            <button
              type="button"
              onClick={handleBack}
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: "#64748b", fontSize: 16, fontWeight: 500, padding: 0, marginRight: 8 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              Back
            </button>
          )}
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a", flex: 1, textAlign: "center" }}>
            {modalTitle}
          </h3>
          <button type="button" onClick={resetAndClose} style={{ background: "none", border: "none", borderRadius: 8, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 18, color: "#94a3b8" }}>
            ✕
          </button>
        </div>

        {/* ═══ BREADCRUMBS ═══ */}
        {(catalogView === "categories" || catalogView === "products") && selectedBrand && (
          <div style={{ padding: "0 24px 10px", display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#94a3b8" }}>
            <span style={{ cursor: "pointer", color: "#3b82f6", fontWeight: 500 }} onClick={() => {
              // Previous flow returned to the Shop Signs picker:
              // setCatalogView("picker");
              setCatalogView("brands");
              setSelectedBrand(null);
              setSelectedCategory(null);
              setCatalogSearch("");
              setCategorySearchInput("");
              setCategorySearch("");
            }}>{shopSignsLabel}</span>
            <span>›</span>
            <span style={{ cursor: "pointer", color: "#3b82f6", fontWeight: 500 }} onClick={() => { setCatalogView("brands"); setSelectedBrand(null); setSelectedCategory(null); setCatalogSearch(""); setCategorySearchInput(""); setCategorySearch(""); }}>Brands</span>
            <span>›</span>
            <span style={{ cursor: catalogView === "products" ? "pointer" : "default", color: catalogView === "products" ? "#3b82f6" : "#334155", fontWeight: 500 }} onClick={() => { if (catalogView === "products") { setCatalogView("categories"); setSelectedCategory(null); setCatalogSearch(""); } }}>{selectedBrand.name}</span>
            {catalogView === "products" && selectedCategory && (
              <><span>›</span><span style={{ color: "#334155", fontWeight: 500 }}>{selectedCategory.name}</span></>
            )}
          </div>
        )}
        {catalogView === "products" && !selectedBrand && selectedCategory && (
          <div style={{ padding: "0 24px 10px", display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#94a3b8" }}>
            <span style={{ cursor: "pointer", color: "#3b82f6", fontWeight: 500 }} onClick={() => {
              // Previous flow returned to the Shop Signs picker:
              // setCatalogView("picker");
              setCatalogView("brands");
              setSelectedCategory(null);
              setCatalogSearch("");
            }}>{shopSignsLabel}</span>
            <span>›</span>
            <span style={{ cursor: "pointer", color: "#3b82f6", fontWeight: 500 }} onClick={() => { setCatalogView("allCategories"); setSelectedCategory(null); setCatalogSearch(""); }}>Categories</span>
            <span>›</span>
            <span style={{ color: "#334155", fontWeight: 500 }}>{selectedCategory.name}</span>
          </div>
        )}

        {/* ═══ NO USER SELECTED ═══ */}
        {noUser ? (
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>👤</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#334155", marginBottom: 6 }}>No agent or office selected</div>
            <div style={{ fontSize: 13, color: "#94a3b8" }}>Please select an agent or office first to view linked products.</div>
          </div>
        ) : (
          <>
            {/* ═══ SEARCH VIEW — dynamic products + Browse Catalog button ═══ */}
            {catalogView === "search" && (
              <>
                {/* Search (short, centered) + Browse Catalog (same row desktop; below search on mobile) */}
                <div style={{ padding: "0 24px 12px" }}>
                  <div className="psm-search-row" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }} />
                    <div className="psm-search-input-wrap" style={{ position: "relative", width: "100%", maxWidth: 380, flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}>
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by product name or SKU... (press Enter)"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            setPagination(prev => ({ ...prev, pageIndex: 0 }));
                            setSearch(searchInput);
                          }
                        }}
                        style={{ width: "100%", padding: "11px 16px 11px 40px", border: "1px solid #e2e8f0", borderRadius: 24, fontSize: 13, outline: "none", background: "#fff", boxSizing: "border-box", color: "#334155" }}
                      />
                    </div>
                    <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                      <button
                        type="button"
                        onClick={handleBrowseCatalog}
                        disabled={catalogCountsLoading || (!slotId && accessoryCatalogLoading)}
                        style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)", color: "#fff", border: "none", borderRadius: 24, padding: "11px 20px", fontSize: 15, fontWeight: 600, cursor: catalogCountsLoading || (!slotId && accessoryCatalogLoading) ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", transition: "opacity 0.15s", opacity: catalogCountsLoading || (!slotId && accessoryCatalogLoading) ? 0.8 : 1 }}
                        onMouseEnter={(e) => { if (!catalogCountsLoading && (slotId || !accessoryCatalogLoading)) e.currentTarget.style.opacity = "0.9"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = catalogCountsLoading || (!slotId && accessoryCatalogLoading) ? "0.8" : "1"; }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                        {catalogCountsLoading || (!slotId && accessoryCatalogLoading) ? "Loading..." : shopSignsLabel}
                      </button>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <CatalogContentLoader label="Loading products..." />
                ) : error ? (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#ef4444", fontSize: 13 }}>
                    <div style={{ marginBottom: 8 }}>Error: {error}</div>
                    <button type="button" onClick={fetchProducts} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Retry</button>
                  </div>
                ) : products.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "50px 0", color: "#94a3b8", fontSize: 13 }}>
                    {search ? "No products match your search." : "No linked products found."}
                  </div>
                ) : (
                  <>
                    {/* ── Desktop table (same structure as ordercheckoutpop: pp-table-container + pp-table) ── */}
                    <div className="psm-desktop">
                      <div className="pp-table-container psm-table-container" style={{ maxHeight: 620 }}>
                        <table className="pp-table psm-products-table" style={{ tableLayout: "fixed", width: "100%" }}>
                          <colgroup>
                            <col style={{ width: "10%" }} />
                            <col style={{ width: "38%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "10%" }} />
                            <col style={{ width: "10%" }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th><div className="table-headers text-center">Image</div></th>
                              <th><div className="table-headers">Product Name</div></th>
                              <th><div className="table-headers">SKU</div></th>
                              <th><div className="table-headers">Category</div></th>
                              <th><div className="table-headers text-center">Avl Qty</div></th>
                              <th><div className="table-headers text-center">Action</div></th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.map((product, i) => {
                              const isSelected = selectedProductId === product.product_id;
                              const baseAvailable = Number(product.available_quantity) || 0;
                              const remainingQty = getRemainingQty(
                                baseAvailable,
                                product.product_id,
                                product.variant_id
                              );
                              const inStock = remainingQty > 0;
                              return (
                                <tr key={product.product_id + "-" + i} style={{ background: isSelected ? "#eff6ff" : i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                  <td><div className="table-cells text-center">{renderProductImage(product)}</div></td>
                                  <td><div className="table-cells table-cells-product-name fw-bold" title={product.name}>{product.name}</div></td>
                                  <td><div className="table-cells fw-medium">{product.sku}</div></td>
                                  <td>
                                    <div className="table-cells">
                                      {product.category ? (
                                        <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 14, fontWeight: 600, background: "#dcfce7", color: "#16a34a" }}>{product.category}</span>
                                      ) : (
                                        <span style={{ fontSize: 14, color: "#cbd5e1" }}>—</span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="table-cells text-center">
                                      <span style={{ fontWeight: 600, color: inStock ? "#334155" : "#ef4444" }}>{remainingQty.toLocaleString()}</span>
                                      {!inStock && <div style={{ fontSize: 13, color: "#ef4444", fontWeight: 500, marginTop: 1 }}>Out of stock</div>}
                                    </div>
                                  </td>
                                  <td><div className="table-cells text-center">{renderActionButton(product, isSelected, baseAvailable, remainingQty)}</div></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* ── Mobile cards (<768px) ── */}
                    <div className="psm-mobile">
                      <div style={{ padding: "8px 16px", overflow: "auto", maxHeight: 420 }}>
                        {products.map((product, i) => {
                          const isSelected = selectedProductId === product.product_id;
                          const baseAvailable = Number(product.available_quantity) || 0;
                          const remainingQty = getRemainingQty(
                            baseAvailable,
                            product.product_id,
                            product.variant_id
                          );
                          const inStock = remainingQty > 0;
                          return (
                            <div
                              key={product.product_id + "-m-" + i}
                              style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 14, marginBottom: 10, background: isSelected ? "#eff6ff" : "#fff", transition: "box-shadow 0.15s" }}
                            >
                              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                <div style={{ flexShrink: 0 }}>{renderProductImage(product)}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 7 }}>{product.name}</div>
                                  <div style={{ fontSize: 13, color: "#5b5c5e", marginBottom: 6 }}>SKU: {product.sku}</div>
                                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                                    <div>
                                      <div style={{ fontSize: 13, color: "#5b5c5e", fontWeight: 500 }}>Category</div>
                                      {product.category ? (
                                        <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 600, background: "#dcfce7", color: "#16a34a", marginTop: 2 }}>{product.category}</span>
                                      ) : (
                                        <span style={{ fontSize: 13, color: "#cbd5e1" }}>—</span>
                                      )}
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 13, color: "#5b5c5e", fontWeight: 500 }}>Avl Qty</div>
                                      <span style={{ fontSize: 13, fontWeight: 600, color: inStock ? "#334155" : "#ef4444" }}>{remainingQty.toLocaleString()}</span>
                                    </div>
                                  </div>
                                </div>
                                <div style={{ flexShrink: 0, alignSelf: "center" }}>{renderActionButton(product, isSelected, baseAvailable, remainingQty)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* ═══ BROWSE CATALOG PICKER — counts from API when slotId provided, else static ═══ */}
            {catalogView === "picker" && (() => {
              return (
                <div style={{ flex: 1, overflow: "auto", padding: "16px 24px", maxHeight: 420 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                    {/* Brands card — count in orange */}
                    <div
                      onClick={() => setCatalogView("brands")}
                      style={{ border: "2px solid #e2e8f0", borderRadius: 14, padding: "28px 16px", cursor: "pointer", transition: "all 0.2s", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#f59e0b"; e.currentTarget.style.background = "#fffdf5"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.06)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <div style={{ width: 56, height: 56, borderRadius: 14, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🏷️</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b" }}>Brands</div>
                      <div style={{ fontSize: 15, color: "#6b7280" }}>Select by brand</div>
                    </div>
                    {/* Categories card — count in blue */}
                    <div
                      onClick={() => setCatalogView("allCategories")}
                      style={{ border: "2px solid #e2e8f0", borderRadius: 14, padding: "28px 16px", cursor: "pointer", transition: "all 0.2s", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.background = "#f0f7ff"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.06)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <div style={{ width: 56, height: 56, borderRadius: 14, background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>📂</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b" }}>Categories</div>
                      <div style={{ fontSize: 15, color: "#6b7280" }}>Select by category</div>
                    </div>
                    {/* Products card — count in green */}
                    <div
                      onClick={() => { setCatalogView("allProducts"); setCatalogSearch(""); }}
                      style={{ border: "2px solid #e2e8f0", borderRadius: 14, padding: "28px 16px", cursor: "pointer", transition: "all 0.2s", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10 }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#22c55e"; e.currentTarget.style.background = "#f0fdf4"; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.06)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <div style={{ width: 56, height: 56, borderRadius: 14, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>📦</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b" }}>Products</div>
                      <div style={{ fontSize: 15, color: "#6b7280" }}>Pick individual products</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ═══ BRANDS GRID — dynamic when slot (select2) or accessory (bulk-by-ids), else empty ═══ */}
            {catalogView === "brands" && (() => {
              const useDynamicBrands = canLoadSlotBrands || canLoadAccessoryBrands;
              const brandsToShow = useDynamicBrands
                ? slotBrandsItems.map((b): Brand => ({
                    id: b._id,
                    name: b.name || b.text || "",
                    logo: (b.name || b.text || "?")[0].toUpperCase(),
                    color: "#f59e0b",
                    bg: "#fef3c7",
                    categories: b.categories,
                    product_count: b.product_count,
                    image: pickRawImageValue(b as Record<string, unknown>),
                  }))
                : [];
              return (
                <div ref={brandsScrollRef} style={{ flex: 1, overflow: "auto", padding: "4px 24px 16px", maxHeight: 420 }}>
                  <CatalogCenteredSearch
                    value={brandSearchInput}
                    committedValue={brandSearch}
                    onChange={setBrandSearchInput}
                    onCommit={setBrandSearch}
                    placeholder="Search brands..."
                  />
                  {useDynamicBrands && slotBrandsLoading && brandsToShow.length === 0 ? (
                    <CatalogContentLoader label="Loading brands..." />
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12 }}>
                        {brandsToShow.map((brand) => {
                      const brandImageSrc =
                        resolvePresignedImageSrc(brand.image, brandImageMap, imagecustomRounded) ||
                        imagecustomRounded;
                      return (
                        <div key={brand.id}
                          onClick={() => {
                            setSelectedBrand(brand);
                            setCategorySearchInput("");
                            setCategorySearch("");
                            setCatalogView("categories");
                          }}
                          style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 10px 12px", cursor: "pointer", transition: "all 0.2s", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10, minHeight: 150 }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = brand.color; e.currentTarget.style.background = brand.bg; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.08)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <div style={{ width: 100, height: 78,display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                            <img
                              src={brandImageSrc}
                              alt={brand.name}
                              style={{ width: "100%", height: "100%", objectFit: "contain" }}
                              onError={(e) => {
                                if (e.currentTarget.src !== imagecustomRounded) {
                                  e.currentTarget.src = imagecustomRounded;
                                }
                              }}
                            />
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", lineHeight: 1.25 }}>{brand.name}</div>
                        </div>
                      );
                    })}
                      </div>
                      {!slotBrandsLoading && brandsToShow.length === 0 && (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>
                          {brandSearch ? "No brands match your search." : "No brands found."}
                        </div>
                      )}
                      {useDynamicBrands && slotBrandsLoading && brandsToShow.length > 0 && (
                        <div style={{ textAlign: "center", padding: 12 }}>
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading more brands...</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {useDynamicBrands && <div ref={brandsSentinelRef} style={{ height: 1, visibility: "hidden" }} aria-hidden />}
                </div>
              );
            })()}

            {/* ═══ ALL CATEGORIES GRID — dynamic when slot (select2) or accessory (bulk-by-ids), else empty ═══ */}
            {catalogView === "allCategories" && (() => {
              const useDynamicCategories = canLoadSlotCategories || canLoadAccessoryCategories;
              const categoriesToShow = useDynamicCategories
                ? slotCategoriesItems.map((c): Category => {
                    const style = getCatStyle(c.name || c.text || "");
                    return {
                      id: c._id,
                      name: c.name || c.text || "",
                      icon: (c.name || c.text || "?")[0].toUpperCase(),
                      color: style.color,
                      bg: style.bg,
                      description: "",
                      product_count: c.product_count,
                      image: pickRawImageValue(c as Record<string, unknown>),
                    };
                  })
                : [];
              return (
                <div ref={categoriesScrollRef} style={{ flex: 1, overflow: "auto", padding: "4px 24px 16px", maxHeight: 420 }}>
                  <CatalogCenteredSearch
                    value={categorySearchInput}
                    committedValue={categorySearch}
                    onChange={setCategorySearchInput}
                    onCommit={setCategorySearch}
                    placeholder="Search categories..."
                  />
                  {useDynamicCategories && slotCategoriesLoading && categoriesToShow.length === 0 ? (
                    <CatalogContentLoader label="Loading categories..." />
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12 }}>
                        {categoriesToShow.map((cat) => {
                      const categoryImageSrc =
                        resolvePresignedImageSrc(cat.image, categoryImageMap, imagecustomRounded) ||
                        imagecustomRounded;
                      return (
                        <div key={cat.id}
                          onClick={() => { setSelectedCategory(cat); setCatalogView("products"); setSelectedBrand(null); setCatalogSearch(""); }}
                          style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 10px 12px", cursor: "pointer", transition: "all 0.2s", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10, minHeight: 150 }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.background = cat.bg; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.08)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <div style={{ width: 100, height: 78, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                            <img
                              src={categoryImageSrc}
                              alt={cat.name}
                              style={{ width: "100%", height: "100%", objectFit: "contain" }}
                              onError={(e) => {
                                if (e.currentTarget.src !== imagecustomRounded) {
                                  e.currentTarget.src = imagecustomRounded;
                                }
                              }}
                            />
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", lineHeight: 1.25 }}>{cat.name}</div>
                        </div>
                      );
                    })}
                      </div>
                      {!slotCategoriesLoading && categoriesToShow.length === 0 && (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>
                          {categorySearch ? "No categories match your search." : "No categories found."}
                        </div>
                      )}
                      {useDynamicCategories && slotCategoriesLoading && categoriesToShow.length > 0 && (
                        <div style={{ textAlign: "center", padding: 12 }}>
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading more categories...</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {useDynamicCategories && <div ref={categoriesSentinelRef} style={{ height: 1, visibility: "hidden" }} aria-hidden />}
                </div>
              );
            })()}

            {/* ═══ CATEGORIES GRID (for selected brand) — slot: slot brand-categories API; accessory: accessories/.../brands/{id}/categories/select2 ═══ */}
            {catalogView === "categories" && selectedBrand && (() => {
              const categoriesByBrand = Boolean(selectedBrand);
              const useDynamicCategories = categoriesByBrand || canLoadSlotCategories || canLoadAccessoryCategories;
              const mapCat = (c: SlotSelect2Category): Category => {
                const style = getCatStyle(c.name || c.text || "");
                return {
                  id: c._id,
                  name: c.name || c.text || "",
                  icon: (c.name || c.text || "?")[0].toUpperCase(),
                  color: style.color,
                  bg: style.bg,
                  description: "",
                  product_count: c.product_count,
                  image: pickRawImageValue(c as Record<string, unknown>),
                };
              };
              const categoriesToShow =
                categoriesByBrand && slotCategoriesItems.length
                  ? slotCategoriesItems.map(mapCat)
                  : useDynamicCategories && slotCategoriesItems.length && selectedBrand.categories?.length
                    ? slotCategoriesItems.filter((c) => selectedBrand!.categories!.includes(c._id)).map(mapCat)
                    : useDynamicCategories && slotCategoriesItems.length
                      ? slotCategoriesItems.map(mapCat)
                      : [];
              return (
                <div ref={categoriesScrollRef} style={{ flex: 1, overflow: "auto", padding: "4px 24px 16px", maxHeight: 420 }}>
                  <CatalogCenteredSearch
                    value={categorySearchInput}
                    committedValue={categorySearch}
                    onChange={setCategorySearchInput}
                    onCommit={setCategorySearch}
                    placeholder="Search categories..."
                  />
                  {useDynamicCategories && slotCategoriesLoading && categoriesToShow.length === 0 ? (
                    <CatalogContentLoader label="Loading categories..." />
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12 }}>
                        {categoriesToShow.map((cat) => {
                      const categoryImageSrc =
                        resolvePresignedImageSrc(cat.image, categoryImageMap, imagecustomRounded) ||
                        imagecustomRounded;
                      return (
                        <div key={cat.id}
                          onClick={() => { setSelectedCategory(cat); setCatalogView("products"); setCatalogSearch(""); }}
                          style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 10px 12px", cursor: "pointer", transition: "all 0.2s", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10, minHeight: 150 }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.background = cat.bg; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.08)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <div style={{ width: 100, height: 78, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                            <img
                              src={categoryImageSrc}
                              alt={cat.name}
                              style={{ width: "100%", height: "100%", objectFit: "contain" }}
                              onError={(e) => {
                                if (e.currentTarget.src !== imagecustomRounded) {
                                  e.currentTarget.src = imagecustomRounded;
                                }
                              }}
                            />
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", lineHeight: 1.25 }}>{cat.name}</div>
                        </div>
                      );
                    })}
                      </div>
                      {!slotCategoriesLoading && categoriesToShow.length === 0 && (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>
                          {categorySearch ? "No categories match your search." : "No categories found."}
                        </div>
                      )}
                      {useDynamicCategories && slotCategoriesLoading && categoriesToShow.length > 0 && (
                        <div style={{ textAlign: "center", padding: 12 }}>
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading more categories...</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {useDynamicCategories && <div ref={categoriesSentinelRef} style={{ height: 1, visibility: "hidden" }} aria-hidden />}
                </div>
              );
            })()}

            {/* ═══ SEARCH BAR for products / allProducts views ═══ */}
            {(catalogView === "products" || catalogView === "allProducts") && (
              <div style={{ padding: "0 24px 10px" }}>
                <CatalogCenteredSearch
                  value={catalogSearchInput}
                  committedValue={catalogSearch}
                  onChange={setCatalogSearchInput}
                  onCommit={(next) => {
                    setSlotProductsPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    setCatalogSearch(next);
                  }}
                  placeholder={`Search in ${selectedCategory?.name || "products"}...`}
                />
              </div>
            )}

            {/* ═══ CATALOG PRODUCT TABLE (products / allProducts) — slot products, products by category, or accessory products ═══ */}
            {(catalogView === "products" || catalogView === "allProducts") && (slotId || (catalogView === "products" && selectedCategory) || (!slotId && postTypeId)) && (
              <>
                {/* Products: datatable (desktop table + mobile cards) with pagination — from slot API or products_user_filter by category */}
                {slotCatalogProductsLoading ? (
                      <CatalogContentLoader label="Loading products..." />
                    ) : (
                      <>
                        {slotCatalogProducts.length === 0 ? (
                          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No products found.</div>
                        ) : (
                          <>
                            {/* Desktop: same UI/structure as ordercheckoutpop — pp-table-container, pp-table, headers/cells from column defs */}
                            <div className="psm-desktop psm-slot-table-wrap">
                              <div className="pp-table-container psm-table-container" style={{ maxHeight: 620 }}>
                                <table className="pp-table psm-products-table" style={{ tableLayout: "fixed", width: "100%" }}>
                                  <colgroup>
                                    <col style={{ width: "8%" }} />
                                    <col style={{ width: "22%" }} />
                                    <col style={{ width: "12%" }} />
                                    <col style={{ width: "8%" }} />
                                    <col style={{ width: "18%" }} />
                                    <col style={{ width: "14%" }} />
                                    <col style={{ width: "10%" }} />
                                  </colgroup>
                                  <thead>
                                    {slotProductsTable.getHeaderGroups().map((hg) => (
                                      <tr key={hg.id}>
                                        {hg.headers.map((h) => (
                                          <th key={h.id}>
                                            {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                                          </th>
                                        ))}
                                      </tr>
                                    ))}
                                  </thead>
                                  <tbody>
                                    {slotProductsTable.getRowModel().rows.map((row) => {
                                      const r = row.original;
                                      const rowId = r._id;
                                      const variants = r?.product?.variants ?? [];
                                      const hasCustomize = slotProductHasCustomization(variants);
                                      const isExpanded = customizeProductId === rowId;
                                      const selectedAttrs = slotProductSelectedAttributes[rowId] ?? {};
                                      const selectedVariant = findSlotVariantByAttributes(variants, selectedAttrs) ?? variants[0];
                                      return (
                                        <React.Fragment key={row.id}>
                                          <tr className={isExpanded ? "pp-row-expanded" : ""}>
                                            {row.getVisibleCells().map((cell) => (
                                              <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                              </td>
                                            ))}
                                          </tr>
                                          {isExpanded && hasCustomize && (() => {
                                            const customizationFields = getSlotCustomizationFields(selectedVariant, variants);
                                            const customValues = rowCustomValues[rowId] ?? {};
                                            const requiredCustomizationFields = customizationFields.filter((field) =>
                                              Boolean(field?.required)
                                            );
                                            const hasRequiredCustomizationField = requiredCustomizationFields.length > 0;
                                            const canAddFromCustomizationPanel =
                                              !hasRequiredCustomizationField ||
                                              requiredCustomizationFields.every((field) =>
                                                fieldCustomizationValueSatisfied(field, customValues[field.field_name])
                                              );
                                            const invListPanel =
                                              selectedVariant &&
                                              (selectedVariant as { inventory?: { available?: number }[] }).inventory;
                                            const baseQtyPanel = invListPanel
                                              ? invListPanel.reduce((s, inv) => s + (Number(inv.available) || 0), 0)
                                              : 0;
                                            const vidPanel = String(
                                              (selectedVariant as { variant_id?: string })?.variant_id ?? ""
                                            ).trim();
                                            const remainingPanel = getRemainingQty(
                                              baseQtyPanel,
                                              r._id,
                                              vidPanel
                                            );
                                            const canAddByStockPanel = remainingPanel > 0;
                                            if (customizationFields.length === 0) return null;
                                            return (
                                            <tr key={`custom-${row.id}`} className="psm-customization-row">
                                              <td colSpan={row.getVisibleCells().length}>
                                                <div className="psm-customization-panel">
                                                  <div className="psm-customization-title">
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                    Product customization
                                                  </div>
                                                  <div className="psm-customization-form">
                                                    {customizationFields.map((field, fieldIdx) => {
                                                      const fieldKey = `psm-cust-${rowId}-${fieldIdx}-${String(field.field_name || "field")}`;
                                                      const inputId = `psm-file-${rowId}-${field.field_name}`;
                                                      return (
                                                        <div key={fieldKey} className="psm-form-group">
                                                          <label className="psm-form-label">
                                                            {field.field_name}
                                                            {field.required && <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>}
                                                          </label>
                                                          {(field.field_type ?? "text") === "text" ? (
                                                            <input
                                                              type="text"
                                                              className="psm-form-input"
                                                              placeholder={`Enter ${field.field_name}`}
                                                              value={(() => {
                                                                const tv = customValues[field.field_name];
                                                                return typeof tv === "string" ? tv : "";
                                                              })()}
                                                              onChange={(e) => setRowCustomValues((prev) => ({ ...prev, [rowId]: { ...(prev[rowId] ?? {}), [field.field_name]: e.target.value } }))}
                                                            />
                                                          ) : (
                                                            <>
                                                              <div
                                                                className="psm-file-display"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => {
                                                                  if (customizationPanelSubmittingRowId === rowId) return;
                                                                  document.getElementById(inputId)?.click();
                                                                }}
                                                              >
                                                                <span className="psm-file-name">
                                                                  {(() => {
                                                                    const fv = customValues[field.field_name];
                                                                    if (typeof fv === "string" && isHexId(fv.trim())) {
                                                                      return "Image attached — click to replace";
                                                                    }
                                                                    if (fv instanceof File) {
                                                                      return "File selected — click to replace";
                                                                    }
                                                                    return "Click to upload image or PDF";
                                                                  })()}
                                                                </span>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                                                              </div>
                                                              <input
                                                                id={inputId}
                                                                type="file"
                                                                accept="image/*,.pdf"
                                                                style={{ display: "none" }}
                                                                onChange={(e) => {
                                                                  const f = e.target.files?.[0] ?? null;
                                                                  if (f) handleCustomizationFilePick(rowId, field.field_name, f);
                                                                  e.currentTarget.value = "";
                                                                }}
                                                              />
                                                              {(() => {
                                                                const fv = customValues[field.field_name];
                                                                const blobSrc = rowCustomizationFilePreviews[rowId]?.[field.field_name];
                                                                const m = (imageUrlMap as Record<string, string>) || {};
                                                                const fid = typeof fv === "string" ? fv.trim() : "";
                                                                const hexSrc = fid && isHexId(fid) ? m[fid] || getImageSrc(fid, m) || "" : "";
                                                                if (blobSrc && fv instanceof File && fv.type === "application/pdf") {
                                                                  return (
                                                                    <div className="psm-custom-file-preview-wrap">
                                                                      <div
                                                                        style={{
                                                                          padding: 24,
                                                                          borderRadius: 8,
                                                                          border: "1px solid #e5e7eb",
                                                                          background: "#f9fafb",
                                                                          fontSize: 13,
                                                                          color: "#475569",
                                                                        }}
                                                                      >
                                                                        PDF selected: <strong>{fv.name}</strong>
                                                                      </div>
                                                                      <button
                                                                        type="button"
                                                                        className="psm-custom-file-preview-remove"
                                                                        title="Remove file"
                                                                        onClick={(ev) => {
                                                                          ev.stopPropagation();
                                                                          handleCustomizationFilePick(rowId, field.field_name, null);
                                                                        }}
                                                                      >
                                                                        ×
                                                                      </button>
                                                                    </div>
                                                                  );
                                                                }
                                                                const src =
                                                                  blobSrc && fv instanceof File && fv.type.startsWith("image/")
                                                                    ? blobSrc
                                                                    : hexSrc;
                                                                if (!src) return null;
                                                                return (
                                                                  <div className="psm-custom-file-preview-wrap">
                                                                    <img className="psm-custom-file-preview-img" src={src} alt="" />
                                                                    <button
                                                                      type="button"
                                                                      className="psm-custom-file-preview-remove"
                                                                      title="Remove file"
                                                                      onClick={(ev) => {
                                                                        ev.stopPropagation();
                                                                        handleCustomizationFilePick(rowId, field.field_name, null);
                                                                      }}
                                                                    >
                                                                      ×
                                                                    </button>
                                                                  </div>
                                                                );
                                                              })()}
                                                            </>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                  <div className="psm-customization-actions">
                                                    <button type="button" className="psm-cancel-btn" onClick={() => cancelCustomizationRow(rowId)}>Cancel</button>
                                                    <button
                                                      type="button"
                                                      className="psm-add-custom-btn"
                                                      disabled={!canAddFromCustomizationPanel || !canAddByStockPanel || customizationPanelSubmittingRowId === rowId}
                                                      onClick={async () => {
                                                        if (!selectedVariant || customizationPanelSubmittingRowId || !canAddByStockPanel) return;
                                                        setCustomizationPanelSubmittingRowId(rowId);
                                                        try {
                                                          const merged = await mergeRowCustomValuesToUploadedStrings(rowId);
                                                          const vidA = String((selectedVariant as { variant_id?: string })?.variant_id ?? "").trim();
                                                          const bm = studioCustomization?.batchLineItems
                                                            ? findStudioBatchLineItem(studioCustomization.batchLineItems, r._id, vidA)
                                                            : undefined;
                                                          const linked = slotRowToLinkedProduct(r, selectedVariant, bm, merged);
                                                          handleSelectDynamic(linked);
                                                        } catch (err) {
                                                          showError(err instanceof Error ? err.message : "File upload failed");
                                                        } finally {
                                                          setCustomizationPanelSubmittingRowId(null);
                                                        }
                                                      }}
                                                    >
                                                      {customizationPanelSubmittingRowId === rowId ? "Adding…" : "Add"}
                                                    </button>
                                                  </div>
                                                </div>
                                              </td>
                                            </tr>
                                            );
                                          })()}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            {/* Mobile: card view like ordercheckoutpop — image, name, action; then Category, Attributes, Customize; inline customization panel when expanded */}
                            <div className="psm-mobile">
                              <div className="d-flex flex-column gap-3 p-2" style={{ maxHeight: 420, overflowY: "auto", overflowX: "hidden" }}>
                                {slotProductsTable.getRowModel().rows.map((row) => {
                                  const r = row.original;
                                  const rowId = r._id;
                                  const variants = r?.product?.variants ?? [];
                                  const hasCustomize = slotProductHasCustomization(variants);
                                  const isExpanded = customizeProductId === rowId;
                                  const cells = row.getVisibleCells();
                                  const imageCell = cells.find((c) => c.column.id === "image");
                                  const nameCell = cells.find((c) => c.column.id === "name");
                                  const categoryCell = cells.find((c) => c.column.id === "category");
                                  const avlQtyCell = cells.find((c) => c.column.id === "avl_qty");
                                  const attributesCell = cells.find((c) => c.column.id === "attributes");
                                  const customCell = cells.find((c) => c.column.id === "custom");
                                  const actionsCell = cells.find((c) => c.column.id === "actions");
                                  const selectedAttrs = slotProductSelectedAttributes[rowId] ?? {};
                                  const selectedVariant = findSlotVariantByAttributes(variants, selectedAttrs) ?? variants[0];
                                  return (
                                    <div key={row.id} className="card border" style={{ marginBottom: 0 }}>
                                      <div className="card-body" style={{ padding: 14 }}>
                                        <div className="d-flex align-items-start gap-3 mb-3">
                                          {imageCell && (
                                            <div style={{ flexShrink: 0 }}>
                                              {flexRender(imageCell.column.columnDef.cell, imageCell.getContext())}
                                            </div>
                                          )}
                                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            {nameCell && flexRender(nameCell.column.columnDef.cell, nameCell.getContext())}
                                          </div>
                                          {actionsCell && (
                                            <div style={{ flexShrink: 0 }}>
                                              {flexRender(actionsCell.column.columnDef.cell, actionsCell.getContext())}
                                            </div>
                                          )}
                                        </div>
                                        {categoryCell && (
                                          <div style={{ marginBottom: 10 }}>
                                            <div className="psm-mobile-field-label">Category</div>
                                            <div className="psm-mobile-field-value">{flexRender(categoryCell.column.columnDef.cell, categoryCell.getContext())}</div>
                                          </div>
                                        )}
                                        {avlQtyCell && (
                                          <div style={{ marginBottom: 10 }}>
                                            <div className="psm-mobile-field-label">Avl Qty</div>
                                            <div className="psm-mobile-field-value">{flexRender(avlQtyCell.column.columnDef.cell, avlQtyCell.getContext())}</div>
                                          </div>
                                        )}
                                        {attributesCell && (
                                          <div style={{ marginBottom: 10 }}>
                                            <div className="psm-mobile-field-label">Attributes</div>
                                            <div className="attr-titlecase psm-mobile-field-value" style={{ minWidth: 0 }}>{flexRender(attributesCell.column.columnDef.cell, attributesCell.getContext())}</div>
                                          </div>
                                        )}
                                        {customCell && (
                                          <div style={{ marginBottom: 0 }}>
                                            {flexRender(customCell.column.columnDef.cell, customCell.getContext())}
                                          </div>
                                        )}
                                        {isExpanded && hasCustomize && (() => {
                                          const customizationFields = getSlotCustomizationFields(selectedVariant, variants);
                                          const customValues = rowCustomValues[rowId] ?? {};
                                          const requiredCustomizationFields = customizationFields.filter((field) =>
                                            Boolean(field?.required)
                                          );
                                          const hasRequiredCustomizationField = requiredCustomizationFields.length > 0;
                                          const canAddFromCustomizationPanel =
                                            !hasRequiredCustomizationField ||
                                            requiredCustomizationFields.every((field) =>
                                              fieldCustomizationValueSatisfied(field, customValues[field.field_name])
                                            );
                                          const invListPanelMob =
                                            selectedVariant &&
                                            (selectedVariant as { inventory?: { available?: number }[] }).inventory;
                                          const baseQtyPanelMob = invListPanelMob
                                            ? invListPanelMob.reduce((s, inv) => s + (Number(inv.available) || 0), 0)
                                            : 0;
                                          const vidPanelMob = String(
                                            (selectedVariant as { variant_id?: string })?.variant_id ?? ""
                                          ).trim();
                                          const remainingPanelMob = getRemainingQty(
                                            baseQtyPanelMob,
                                            r._id,
                                            vidPanelMob
                                          );
                                          const canAddByStockPanelMob = remainingPanelMob > 0;
                                          if (customizationFields.length === 0) return null;
                                          return (
                                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Product customization</div>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                              {customizationFields.map((field, fieldIdx) => {
                                                const fieldKey = `psm-cust-mob-${rowId}-${fieldIdx}-${String(field.field_name || "field")}`;
                                                const inputId = `psm-mob-file-${rowId}-${field.field_name}`;
                                                return (
                                                  <div key={fieldKey} style={{ minWidth: 0 }}>
                                                    <label style={{ fontSize: 12, fontWeight: 500, color: "#475569", marginBottom: 6, display: "block" }}>
                                                      {field.field_name}
                                                      {field.required && <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>}
                                                    </label>
                                                    {(field.field_type ?? "text") === "text" ? (
                                                      <input type="text" placeholder={`Enter ${field.field_name}`} value={(() => {
                                                        const tv = customValues[field.field_name];
                                                        return typeof tv === "string" ? tv : "";
                                                      })()}
                                                        onChange={(e) => setRowCustomValues((prev) => ({ ...prev, [rowId]: { ...(prev[rowId] ?? {}), [field.field_name]: e.target.value } }))}
                                                        style={{ width: "100%", padding: "10px 14px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, outline: "none", color: "#334155", boxSizing: "border-box", background: "#fff", maxWidth: "100%" }} />
                                                    ) : (
                                                      <>
                                                        <div
                                                          style={{
                                                            border: "1px solid #e2e8f0",
                                                            borderRadius: 8,
                                                            padding: "10px 14px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            cursor: customizationPanelSubmittingRowId === rowId ? "wait" : "pointer",
                                                            background: "#fff",
                                                            minWidth: 0,
                                                          }}
                                                          onClick={() => {
                                                            if (customizationPanelSubmittingRowId === rowId) return;
                                                            document.getElementById(inputId)?.click();
                                                          }}
                                                        >
                                                          <span style={{ fontSize: 12, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {(() => {
                                                              const fv = customValues[field.field_name];
                                                              if (typeof fv === "string" && isHexId(fv.trim())) {
                                                                return "Image attached — tap to replace";
                                                              }
                                                              if (fv instanceof File) {
                                                                return "File selected — tap to replace";
                                                              }
                                                              return "Tap to upload image or PDF";
                                                            })()}
                                                          </span>
                                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                                                        </div>
                                                        <input
                                                          id={inputId}
                                                          type="file"
                                                          accept="image/*,.pdf"
                                                          style={{ display: "none" }}
                                                          onChange={(e) => {
                                                            const f = e.target.files?.[0] ?? null;
                                                            if (f) handleCustomizationFilePick(rowId, field.field_name, f);
                                                            e.currentTarget.value = "";
                                                          }}
                                                        />
                                                        {(() => {
                                                          const fv = customValues[field.field_name];
                                                          const blobSrc = rowCustomizationFilePreviews[rowId]?.[field.field_name];
                                                          const m = (imageUrlMap as Record<string, string>) || {};
                                                          const fid = typeof fv === "string" ? fv.trim() : "";
                                                          const hexSrc = fid && isHexId(fid) ? m[fid] || getImageSrc(fid, m) || "" : "";
                                                          if (blobSrc && fv instanceof File && fv.type === "application/pdf") {
                                                            return (
                                                              <div className="psm-custom-file-preview-wrap" style={{ marginTop: 8 }}>
                                                                <div
                                                                  style={{
                                                                    padding: 24,
                                                                    borderRadius: 8,
                                                                    border: "1px solid #e5e7eb",
                                                                    background: "#f9fafb",
                                                                    fontSize: 13,
                                                                    color: "#475569",
                                                                  }}
                                                                >
                                                                  PDF selected: <strong>{fv.name}</strong>
                                                                </div>
                                                                <button
                                                                  type="button"
                                                                  className="psm-custom-file-preview-remove"
                                                                  title="Remove file"
                                                                  onClick={(ev) => {
                                                                    ev.stopPropagation();
                                                                    handleCustomizationFilePick(rowId, field.field_name, null);
                                                                  }}
                                                                >
                                                                  ×
                                                                </button>
                                                              </div>
                                                            );
                                                          }
                                                          const src =
                                                            blobSrc && fv instanceof File && fv.type.startsWith("image/")
                                                              ? blobSrc
                                                              : hexSrc;
                                                          if (!src) return null;
                                                          return (
                                                            <div className="psm-custom-file-preview-wrap" style={{ marginTop: 8 }}>
                                                              <img className="psm-custom-file-preview-img" src={src} alt="" />
                                                              <button
                                                                type="button"
                                                                className="psm-custom-file-preview-remove"
                                                                title="Remove file"
                                                                onClick={(ev) => {
                                                                  ev.stopPropagation();
                                                                  handleCustomizationFilePick(rowId, field.field_name, null);
                                                                }}
                                                              >
                                                                ×
                                                              </button>
                                                            </div>
                                                          );
                                                        })()}
                                                      </>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                                              <button type="button" onClick={() => cancelCustomizationRow(rowId)}
                                                style={{ background: "#fff", color: "#ef4444", border: "1px solid #ef4444", borderRadius: 8, padding: "8px 18px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                                              <button
                                                type="button"
                                                disabled={!canAddFromCustomizationPanel || !canAddByStockPanelMob || customizationPanelSubmittingRowId === rowId}
                                                onClick={async () => {
                                                  if (!selectedVariant || customizationPanelSubmittingRowId || !canAddByStockPanelMob) return;
                                                  setCustomizationPanelSubmittingRowId(rowId);
                                                  try {
                                                    const merged = await mergeRowCustomValuesToUploadedStrings(rowId);
                                                    const vidA = String((selectedVariant as { variant_id?: string })?.variant_id ?? "").trim();
                                                    const bm = studioCustomization?.batchLineItems
                                                      ? findStudioBatchLineItem(studioCustomization.batchLineItems, r._id, vidA)
                                                      : undefined;
                                                    const linked = slotRowToLinkedProduct(r, selectedVariant, bm, merged);
                                                    handleSelectDynamic(linked);
                                                  } catch (err) {
                                                    showError(err instanceof Error ? err.message : "File upload failed");
                                                  } finally {
                                                    setCustomizationPanelSubmittingRowId(null);
                                                  }
                                                }}
                                                style={{
                                                  background: canAddByStockPanelMob ? "#2563eb" : "#94a3b8",
                                                  color: "#fff",
                                                  border: "none",
                                                  borderRadius: 8,
                                                  padding: "8px 20px",
                                                  fontSize: 12,
                                                  fontWeight: 600,
                                                  cursor:
                                                    canAddFromCustomizationPanel && canAddByStockPanelMob && customizationPanelSubmittingRowId !== rowId
                                                      ? "pointer"
                                                      : "not-allowed",
                                                  opacity:
                                                    canAddFromCustomizationPanel && canAddByStockPanelMob && customizationPanelSubmittingRowId !== rowId ? 1 : 0.6,
                                                }}
                                              >
                                                {customizationPanelSubmittingRowId === rowId ? "Adding…" : "Add"}
                                              </button>
                                            </div>
                                          </div>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                        {/* Pagination: same controls for slot products and category products (datatable) */}
                        <div className="psm-slot-pagination" style={{ borderTop: "1px solid #e2e8f0", padding: "10px 24px", background: "#fafbfc" }}>
                          <PaginationControls table={slotProductsTable} />
                        </div>
                      </>
                    )}
                  </>
                )}

            {/* ═══ FOOTER ═══ */}
            {catalogView === "search" && products.length > 0 ? (
              <div className="px-4 pb-3 pt-2 border-top bg-white">
                <PaginationControls table={table} />
              </div>
            ) : (
              <div style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fafbfc" }}>
                <button
                  type="button"
                  onClick={resetAndClose}
                  style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", color: "#475569", fontSize: 13, fontWeight: 600, padding: "8px 20px", borderRadius: 8, cursor: "pointer" }}
                >Close</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Popup position & size: adjust .psm-modal-overlay (alignment) and .psm-modal-content (width/margin) below */}
      <style>{`
        .psm-search-input-wrap input::placeholder {
          color: #475569;
          opacity: 1;
        }
        .psm-search-input-wrap input::-webkit-input-placeholder {
          color: #475569;
        }
        /* Overlay: how the popup is aligned in the viewport (center = alignItems + justifyContent center) */
        .psm-modal-overlay {
          align-items: center;
          justify-content: center;
          padding-left:14%;
        }
        /* Modal content: width and optional margin/offset */
        .psm-modal-content {
          width: 75%;
          max-width: 75%;
          min-width: 320px;
        }
        @media (max-width: 767px) {
          .psm-modal-overlay {
            align-items: center;
            justify-content: center;
          }
          .psm-modal-content {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            border-radius: 0;
            margin: 0 auto;
          }
        }
        .psm-desktop { display: block; }
        .psm-mobile  { display: none;  }
        /* Tablet and below: cards view; desktop: table view. Search + Browse Catalog: button below search bar. */
        @media (max-width: 991px) {
          .psm-desktop { display: none !important;  }
          .psm-mobile  { display: block !important; }
          .psm-search-row { flex-direction: column; align-items: stretch; }
          .psm-search-row .psm-search-input-wrap { max-width: 100%; }
          .psm-search-row > div:first-child { display: none; }
          .psm-search-row > div:last-child { justify-content: center; }
          .psm-catalog-search-row { flex-direction: column; align-items: stretch; }
          .psm-catalog-search-row .psm-search-input-wrap { max-width: 100%; }
          .psm-catalog-search-row > div:first-child,
          .psm-catalog-search-row > div:last-child { display: none; }
          /* Product tab table (cards): font size and text color for mobile/tablet */
          .psm-mobile .table-cells { font-size: 14px !important; color: #374151; }
          .psm-mobile .table-headers { font-size: 14px !important; color: #6b7280; }
          .psm-mobile .psm-mobile-field-label { font-size: 12px; font-weight: 600; color: #6b7280; margin-bottom: 4px; }
          .psm-mobile .psm-mobile-field-value { font-size: 14px; color: #374151; }
        }
        @media (max-width: 767px) {
          .psm-mobile { overflow-x: hidden; min-width: 0; }
          .psm-mobile select,
          .psm-mobile input,
          .psm-mobile .form-select { max-width: 100%; box-sizing: border-box; }
          .psm-mobile .po-card,
          .psm-mobile .po-field-value { min-width: 0; overflow: hidden; }
          .psm-customization-panel { padding: 12px 16px !important; }
          .psm-customization-form { display: flex !important; flex-direction: column !important; gap: 12px !important; }
          .psm-customization-form > div { min-width: 0; }
          .psm-slot-pagination .pagination-blk { flex-direction: column !important; align-items: stretch !important; gap: 12px !important; margin-top: 8px !important; }
          .psm-slot-pagination .pagination-container { margin-left: 0 !important; justify-content: center; flex-wrap: wrap; }
          .psm-slot-pagination .d-flex.flex-md-row { flex-wrap: wrap; justify-content: center; }
        }
        .psm-slot-products-table { table-layout: fixed; }
        .psm-slot-products-table .production-th,
        .psm-slot-products-table .production-td {
          overflow: hidden;
          padding: 8px 6px;
          vertical-align: middle;
        }
        /* Accordion: row expanded state (like ordercheckoutpop .pp-row-expanded) */
        .psm-row-expanded { background: #fafbfc !important; }
        /* Customization row: panel directly below product row */
        .psm-customization-row td {
          padding: 28px 32px !important;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
        }
        .psm-customization-panel {
          background: #fff;
          border-radius: 10px;
          padding: 24px;
          border: 1.5px solid #e5e7eb;
        }
        .psm-customization-title {
          font-size: 16px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .psm-customization-title svg { color: #6b7280; flex-shrink: 0; }
        .psm-customization-form {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 24px;
        }
        .psm-form-group { display: flex; flex-direction: column; gap: 8px; }
        .psm-form-label {
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          margin: 0;
        }
        .psm-form-input {
          padding: 10px 14px;
          border: 1.5px solid #e5e7eb;
          border-radius: 8px;
          font-size: 14px;
          color: #374151;
          background: #fff;
          box-sizing: border-box;
          width: 100%;
        }
        .psm-form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.08);
        }
        .psm-file-display {
          padding: 10px 14px;
          border: 1.5px dashed #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          color: #6b7280;
          background: #fafbfc;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .psm-file-display:hover { border-color: #3b82f6; background: #f0f9ff; }
        .psm-file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 320px; }
        .psm-custom-file-preview-wrap {
          margin-top: 10px;
          position: relative;
          display: inline-block;
          max-width: 100%;
        }
        .psm-custom-file-preview-img {
          max-height: 160px;
          max-width: 100%;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          object-fit: contain;
          display: block;
          background: #f9fafb;
        }
        .psm-custom-file-preview-remove {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 28px;
          height: 28px;
          border: none;
          border-radius: 999px;
          background: #dc2626;
          color: #fff;
          font-size: 18px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }
        .psm-custom-file-preview-remove:hover {
          background: #b91c1c;
        }
        .psm-customization-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          padding-top: 4px;
        }
        .psm-cancel-btn {
          padding: 10px 24px;
          background: #fff;
          color: #dc2626;
          border: 1.5px solid #dc2626;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        }
        .psm-cancel-btn:hover { background: #dc2626; color: #fff; }
        .psm-add-custom-btn {
          padding: 10px 24px;
          background: #2563eb;
          color: #fff;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        }
        .psm-add-custom-btn:hover:not(:disabled) { background: #1d4ed8; }
        .psm-add-custom-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        /* Attributes: label above dropdown (like ordercheckoutpop .ins-attr-label) */
        .psm-attr-label {
          font-weight: 700;
          font-size: 13px;
          color: #6b7280;
          text-transform: capitalize;
          letter-spacing: 0.02em;
          margin-bottom: 6px;
        }
        .ins-attr-label { font-weight: 700; font-size: 13px; color: #6b7280; text-transform: capitalize; letter-spacing: 0.02em; margin-bottom: 6px; }
        .psm-native-attr-select {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
          padding: 8px 10px;
          font-size: 14px;
          line-height: 1.35;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #fff;
          color: #374151;
          height: 37px;
        }
        .psm-native-attr-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }
        @media (max-width: 991px) {
          .psm-mobile .attr-select { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default ProductSelectionModal;
